import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Users, FileText, Settings, BarChart3, CreditCard, Mail,
  Search, Plus, Edit, Trash2, Eye, CheckCircle, Send,
  Download, Upload, FileSpreadsheet, Award, Phone,
  MessageSquare, Globe, RefreshCw, ArrowUpDown, RotateCcw,
  AlertTriangle, UserX, Building2, ChevronDown, ChevronUp,
  ClipboardList, UserPlus, Clock, History, X, Shield,
  Pencil, Power, Save
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

// Dashboard Overview
const AdminOverview = () => {
  const [stats, setStats] = useState({
    total_members: 0,
    pending_applications: 0,
    active_members: 0,
    pending_payments: 0,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [appsRes, directoryRes] = await Promise.all([
        axios.get(`${API}/admin/applications?page_size=1`, { withCredentials: true }),
        axios.get(`${API}/directory?page_size=1`)
      ]);
      
      setStats(prev => ({
        ...prev,
        pending_applications: appsRes.data.total,
        active_members: directoryRes.data.total
      }));
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const statCards = [
    { label: "Active Members", value: stats.active_members, icon: Users, color: "bg-teal-100 text-teal-700" },
    { label: "Pending Applications", value: stats.pending_applications, icon: FileText, color: "bg-blue-100 text-blue-700" },
    { label: "This Month", value: "-", icon: CheckCircle, color: "bg-green-100 text-green-700" },
    { label: "Pending Renewals", value: "-", icon: RefreshCw, color: "bg-amber-100 text-amber-700" },
  ];

  return (
    <div className="space-y-6">
      <h2 className="font-heading font-bold text-2xl text-slate-900">Dashboard Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                  <p className="font-heading font-bold text-3xl text-slate-900 mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Export Directory</CardTitle>
            <CardDescription>Download member data as Excel</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/admin/directory/export`, {
                    withCredentials: true,
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `cpsi_directory_${new Date().toISOString().split('T')[0]}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success("Directory exported successfully");
                } catch (error) {
                  toast.error("Failed to export directory");
                }
              }}
              variant="outline" 
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Applications</CardTitle>
            <CardDescription>Review pending applications</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/admin/applications">
              <Button variant="outline" className="w-full">
                <FileText className="w-4 h-4 mr-2" />
                View Applications
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Settings</CardTitle>
            <CardDescription>Configure integrations</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/admin/settings">
              <Button variant="outline" className="w-full">
                <Settings className="w-4 h-4 mr-2" />
                Manage Settings
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Applications Management
const ApplicationsManagement = () => {
  const navigate = require('react-router-dom').useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [correctionDialog, setCorrectionDialog] = useState({ open: false, app: null });
  const [correctionText, setCorrectionText] = useState("");
  const [sendingCorrection, setSendingCorrection] = useState(false);
  const [sortBy, setSortBy] = useState("submission_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [detailsModal, setDetailsModal] = useState({ open: false, app: null, documents: [], timeline: [] });
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Release 2 states
  const [statusFilter, setStatusFilter] = useState("all");
  const [secretariatReviewModal, setSecretariatReviewModal] = useState({ open: false, app: null });
  const [checklist, setChecklist] = useState({});
  const [internalNotes, setInternalNotes] = useState("");
  const [structuredCorrectionModal, setStructuredCorrectionModal] = useState({ open: false, app: null });
  const [correctionItems, setCorrectionItems] = useState([]);
  const [additionalComments, setAdditionalComments] = useState("");
  const [committeeAssignModal, setCommitteeAssignModal] = useState({ open: false, app: null });
  const [availableReviewers, setAvailableReviewers] = useState([]);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [expiryHours, setExpiryHours] = useState(72);
  const [finalDecisionModal, setFinalDecisionModal] = useState({ open: false, app: null, committeeDecisions: [] });
  const [finalDecision, setFinalDecision] = useState({ decision: "", comments: "" });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [sortBy, sortOrder, statusFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/applications?sort_by=${sortBy}&sort_order=${sortOrder}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const fetchApplicationDetails = async (app) => {
    setLoadingDetails(true);
    setDetailsModal({ open: true, app, documents: [], timeline: [] });
    try {
      const [detailsRes, timelineRes] = await Promise.all([
        axios.get(`${API}/admin/applications/${app.application_id}/details`, { withCredentials: true }),
        axios.get(`${API}/admin/applications/${app.application_id}/timeline`, { withCredentials: true }).catch(() => ({ data: { timeline: [] } }))
      ]);
      setDetailsModal({ 
        open: true, 
        app: detailsRes.data.application || app, 
        documents: detailsRes.data.documents || [], 
        corrections: detailsRes.data.corrections || [], 
        payment: detailsRes.data.payment, 
        user: detailsRes.data.user,
        timeline: timelineRes.data.timeline || []
      });
    } catch (error) {
      toast.error("Failed to load application details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Release 2: Secretariat Review
  // P1-03: Load saved checklist state on open for persistence across refresh/logout/login
  const openSecretariatReview = async (app) => {
    setSecretariatReviewModal({ open: true, app });
    setInternalNotes("");
    
    // Initialize default checklist
    const defaultChecklist = {
      identity_contact: { 
        full_name: { status: "incomplete", na_reason: "" }, 
        email: { status: "incomplete", na_reason: "" }, 
        phone: { status: "incomplete", na_reason: "" }, 
        address: { status: "incomplete", na_reason: "" }, 
        aadhaar: { status: "incomplete", na_reason: "" } 
      },
      membership_eligibility: { 
        membership_type: { status: "incomplete", na_reason: "" }, 
        eligibility_criteria: { status: "incomplete", na_reason: "" } 
      },
      education_professional: { 
        qualification: { status: "incomplete", na_reason: "" }, 
        institution: { status: "incomplete", na_reason: "" }, 
        rci_status: { status: "incomplete", na_reason: "" }, 
        rci_number: { status: "incomplete", na_reason: "" } 
      },
      required_documents: { 
        photo: { status: "incomplete", na_reason: "" }, 
        aadhaar_front: { status: "incomplete", na_reason: "" }, 
        aadhaar_back: { status: "incomplete", na_reason: "" }, 
        rci_certificate: { status: "incomplete", na_reason: "" }, 
        degree_certificate: { status: "incomplete", na_reason: "" } 
      },
      category_specific: { 
        student_id: { status: "incomplete", na_reason: "" }, 
        work_experience: { status: "incomplete", na_reason: "" }, 
        supervisor_letter: { status: "incomplete", na_reason: "" } 
      }
    };
    
    try {
      // P1-03: Fetch any previously saved checklist state
      const response = await axios.get(`${API}/admin/applications/${app.application_id}/checklist`, { 
        withCredentials: true 
      });
      
      if (response.data.has_saved_state && response.data.checklist) {
        // Restore saved checklist state
        const savedChecklist = {...defaultChecklist};
        for (const item of response.data.checklist) {
          const [section, itemId] = item.item_id.split(".");
          if (savedChecklist[section] && savedChecklist[section][itemId]) {
            savedChecklist[section][itemId] = {
              status: item.status,
              na_reason: item.na_reason || ""
            };
          }
        }
        setChecklist(savedChecklist);
        setInternalNotes(response.data.internal_notes || "");
        toast.info("Loaded previously saved checklist state");
      } else {
        setChecklist(defaultChecklist);
      }
    } catch (error) {
      console.error("Error loading saved checklist:", error);
      setChecklist(defaultChecklist);
    }
  };

  const handleSecretariatReview = async (forwardToCommittee) => {
    setProcessing(true);
    try {
      const checklistItems = [];
      Object.entries(checklist).forEach(([section, items]) => {
        Object.entries(items).forEach(([itemId, data]) => {
          checklistItems.push({ 
            item_id: `${section}.${itemId}`, 
            status: data.status, 
            notes: "",
            na_reason: data.status === "not_applicable" ? data.na_reason : null
          });
        });
      });
      
      await axios.post(`${API}/admin/applications/${secretariatReviewModal.app.application_id}/secretariat-review`, {
        checklist: checklistItems,
        internal_notes: internalNotes,
        forward_to_committee: forwardToCommittee
      }, { withCredentials: true });
      
      toast.success(forwardToCommittee ? "Forwarded to committee" : "Review saved");
      setSecretariatReviewModal({ open: false, app: null });
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save review");
    } finally {
      setProcessing(false);
    }
  };

  // P1-03: Save checklist only (without status change) for draft persistence
  const handleSaveChecklistOnly = async () => {
    setProcessing(true);
    try {
      const checklistItems = [];
      Object.entries(checklist).forEach(([section, items]) => {
        Object.entries(items).forEach(([itemId, data]) => {
          checklistItems.push({ 
            item_id: `${section}.${itemId}`, 
            status: data.status, 
            na_reason: data.status === "not_applicable" ? data.na_reason : null
          });
        });
      });
      
      await axios.post(`${API}/admin/applications/${secretariatReviewModal.app.application_id}/checklist`, {
        checklist: checklistItems,
        internal_notes: internalNotes
      }, { withCredentials: true });
      
      toast.success("Checklist saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save checklist");
    } finally {
      setProcessing(false);
    }
  };

  // Release 2: Structured Correction
  const openStructuredCorrection = (app) => {
    setStructuredCorrectionModal({ open: true, app });
    setCorrectionItems([{ field_or_document: "", is_mandatory: false, comment: "" }]);
    setAdditionalComments("");
  };

  const addCorrectionItem = () => {
    setCorrectionItems([...correctionItems, { field_or_document: "", is_mandatory: false, comment: "" }]);
  };

  const updateCorrectionItem = (index, field, value) => {
    const updated = [...correctionItems];
    updated[index][field] = value;
    setCorrectionItems(updated);
  };

  const removeCorrectionItem = (index) => {
    setCorrectionItems(correctionItems.filter((_, i) => i !== index));
  };

  const handleStructuredCorrection = async () => {
    const validItems = correctionItems.filter(i => i.field_or_document && i.comment);
    if (validItems.length === 0) {
      toast.error("Add at least one correction item with comment");
      return;
    }
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/applications/${structuredCorrectionModal.app.application_id}/structured-correction`, {
        items: validItems,
        additional_comments: additionalComments
      }, { withCredentials: true });
      toast.success("Correction request sent to applicant");
      setStructuredCorrectionModal({ open: false, app: null });
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send correction request");
    } finally {
      setProcessing(false);
    }
  };

  // Release 2: Committee Assignment
  const openCommitteeAssign = async (app) => {
    setCommitteeAssignModal({ open: true, app });
    setSelectedReviewers([]);
    setExpiryHours(72);
    try {
      const response = await axios.get(`${API}/admin/committee-reviewers`, { withCredentials: true });
      setAvailableReviewers(response.data.reviewers || []);
    } catch (error) {
      toast.error("Failed to load reviewers");
    }
  };

  const handleAssignCommittee = async () => {
    if (selectedReviewers.length === 0) {
      toast.error("Select at least one reviewer");
      return;
    }
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/applications/${committeeAssignModal.app.application_id}/assign-committee`, {
        reviewer_ids: selectedReviewers,
        expiry_hours: expiryHours
      }, { withCredentials: true });
      toast.success("Assigned to committee reviewers");
      setCommitteeAssignModal({ open: false, app: null });
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign committee");
    } finally {
      setProcessing(false);
    }
  };

  // Release 2: Final Decision
  const openFinalDecision = async (app) => {
    setFinalDecisionModal({ open: true, app, committeeDecisions: [] });
    setFinalDecision({ decision: "", comments: "" });
    try {
      const response = await axios.get(`${API}/admin/applications/${app.application_id}/committee-decisions`, { withCredentials: true });
      setFinalDecisionModal({ open: true, app, committeeDecisions: response.data.decisions || [] });
    } catch (error) {
      console.error("Failed to load committee decisions");
    }
  };

  const handleFinalDecision = async () => {
    if (!finalDecision.decision) {
      toast.error("Select a decision");
      return;
    }
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/applications/${finalDecisionModal.app.application_id}/secretariat-final-decision`, {
        decision: finalDecision.decision,
        comments: finalDecision.comments
      }, { withCredentials: true });
      toast.success(`Application ${finalDecision.decision}ed`);
      setFinalDecisionModal({ open: false, app: null, committeeDecisions: [] });
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit decision");
    } finally {
      setProcessing(false);
    }
  };

  // Release 3: Send Payment Link
  const handleSendPaymentLinkR3 = async (app) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/applications/${app.application_id}/payment-link-r3`, {
        application_id: app.application_id
      }, { withCredentials: true });
      toast.success(`Payment link sent! Order ID: ${response.data.order_id}`);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send payment link");
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async (app) => {
    try {
      await axios.post(`${API}/admin/applications/${app.application_id}/approve`, {}, { withCredentials: true });
      toast.success("Membership approved!");
      fetchApplications();
    } catch (error) {
      toast.error("Error approving membership");
    }
  };

  const handleSendPaymentLink = async (app) => {
    try {
      const response = await axios.post(
        `${API}/admin/applications/${app.application_id}/payment-link`,
        {},
        { withCredentials: true }
      );
      toast.success(`Payment link sent to applicant`);
      fetchApplications();
    } catch (error) {
      toast.error("Error generating payment link");
    }
  };

  const handleGenerateCertificate = async (userId) => {
    try {
      await axios.post(`${API}/admin/generate-certificate/${userId}`, {}, { withCredentials: true });
      toast.success("Certificate generated and email sent!");
    } catch (error) {
      toast.error("Error generating certificate");
    }
  };

  const handleRequestCorrection = async () => {
    if (!correctionText.trim()) {
      toast.error("Please enter correction details");
      return;
    }
    
    setSendingCorrection(true);
    try {
      await axios.post(
        `${API}/admin/applications/${correctionDialog.app.application_id}/request-correction`,
        { correction_comments: correctionText },
        { withCredentials: true }
      );
      toast.success("Correction request sent to applicant");
      setCorrectionDialog({ open: false, app: null });
      setCorrectionText("");
      fetchApplications();
    } catch (error) {
      toast.error("Error sending correction request");
    } finally {
      setSendingCorrection(false);
    }
  };

  const handleUpdateStatus = async (app, newStatus) => {
    try {
      await axios.post(
        `${API}/admin/applications/${app.application_id}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchApplications();
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      // Release 2 statuses
      submitted: { label: "Submitted", color: "bg-blue-100 text-blue-800" },
      under_secretariat_review: { label: "Secretariat Review", color: "bg-purple-100 text-purple-800" },
      correction_requested: { label: "Correction Requested", color: "bg-amber-100 text-amber-800" },
      resubmitted: { label: "Resubmitted", color: "bg-cyan-100 text-cyan-800" },
      complete_pending_committee: { label: "Ready for Committee", color: "bg-indigo-100 text-indigo-800" },
      under_committee_review: { label: "Committee Review", color: "bg-violet-100 text-violet-800" },
      committee_recommended: { label: "Recommended", color: "bg-emerald-100 text-emerald-800" },
      committee_hold: { label: "On Hold", color: "bg-yellow-100 text-yellow-800" },
      committee_rejected: { label: "Committee Rejected", color: "bg-rose-100 text-rose-800" },
      secretariat_accepted: { label: "Accepted", color: "bg-teal-100 text-teal-800" },
      secretariat_rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
      // Legacy statuses
      under_scrutiny: { label: "Under Scrutiny", color: "bg-purple-100 text-purple-800" },
      approved_pending_payment: { label: "Pending Payment", color: "bg-orange-100 text-orange-800" },
      payment_completed: { label: "Payment Received", color: "bg-blue-100 text-blue-800" },
      active: { label: "Active", color: "bg-teal-100 text-teal-800" },
      rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
      needs_correction: { label: "Needs Correction", color: "bg-amber-100 text-amber-800" },
    };
    const conf = config[status] || { label: status?.replace(/_/g, ' '), color: "bg-slate-100 text-slate-800" };
    return <Badge className={`${conf.color} font-medium`}>{conf.label}</Badge>;
  };

  const renderSortIcon = (field) => (
    <>
      <ArrowUpDown className={`w-3 h-3 ${sortBy === field ? 'text-slate-900' : 'text-slate-400'}`} />
      {sortBy === field && (
        <span className="text-xs text-slate-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-slate-900">Applications</h2>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_secretariat_review">Under Secretariat Review</SelectItem>
              <SelectItem value="correction_requested">Correction Requested</SelectItem>
              <SelectItem value="resubmitted">Resubmitted</SelectItem>
              <SelectItem value="complete_pending_committee">Pending Committee</SelectItem>
              <SelectItem value="under_committee_review">Under Committee Review</SelectItem>
              <SelectItem value="committee_recommended">Recommended</SelectItem>
              <SelectItem value="committee_hold">On Hold</SelectItem>
              <SelectItem value="committee_rejected">Rejected by Committee</SelectItem>
              <SelectItem value="secretariat_accepted">Accepted</SelectItem>
              <SelectItem value="secretariat_rejected">Rejected</SelectItem>
              <SelectItem value="under_scrutiny">Under Scrutiny (Legacy)</SelectItem>
              <SelectItem value="needs_correction">Needs Correction (Legacy)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchApplications}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort("name")}>
                    <div className="flex items-center gap-1">Applicant {renderSortIcon("name")}</div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort("membership_type")}>
                    <div className="flex items-center gap-1">Type {renderSortIcon("membership_type")}</div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort("status")}>
                    <div className="flex items-center gap-1">Status {renderSortIcon("status")}</div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-slate-50 select-none" onClick={() => handleSort("submission_date")}>
                    <div className="flex items-center gap-1">Submitted {renderSortIcon("submission_date")}</div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No applications found
                    </TableCell>
                  </TableRow>
                ) : applications.map((app) => (
                  <TableRow key={app.application_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{app.user_name || "N/A"}</p>
                        <p className="text-sm text-slate-500">{app.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{app.membership_type?.replace('_', ' ')}</TableCell>
                    <TableCell>{getStatusBadge(app.status)}</TableCell>
                    <TableCell>
                      {app.submission_date ? new Date(app.submission_date).toLocaleDateString() : "N/A"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" title="View Details" onClick={() => fetchApplicationDetails(app)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {/* Admin/Secretariat Edit Application */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          title="Edit Application" 
                          onClick={() => navigate(`/application?edit=${app.application_id}`)}
                          className="text-blue-600"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        {/* Release 2: Secretariat Review for new/resubmitted applications */}
                        {["submitted", "resubmitted", "under_secretariat_review", "under_scrutiny"].includes(app.status) && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openSecretariatReview(app)}
                              className="text-purple-600"
                              title="Secretariat Review"
                            >
                              <ClipboardList className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openStructuredCorrection(app)}
                              className="text-amber-600"
                              title="Request Corrections"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        {/* Release 2: Assign to Committee for complete applications */}
                        {app.status === "complete_pending_committee" && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openCommitteeAssign(app)}
                            className="text-indigo-600"
                            title="Assign to Committee"
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Release 2: Final Decision for committee-reviewed applications */}
                        {["committee_recommended", "committee_hold", "committee_rejected", "under_committee_review"].includes(app.status) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openFinalDecision(app)}
                            className="text-teal-700"
                            title="Final Decision"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Correction requested - awaiting resubmission */}
                        {["correction_requested", "needs_correction"].includes(app.status) && (
                          <span className="text-xs text-amber-600 px-2">Awaiting resubmission</span>
                        )}
                        
                        {/* Legacy: Payment pending actions */}
                        {app.status === "approved_pending_payment" && (
                          <Button variant="ghost" size="sm" onClick={() => handleSendPaymentLink(app)} title="Send Payment Link">
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Legacy: Payment completed - Final approve */}
                        {app.status === "payment_completed" && (
                          <Button variant="ghost" size="sm" onClick={() => handleApprove(app)} className="text-teal-700" title="Activate Membership">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Active - Generate certificate */}
                        {app.status === "active" && (
                          <Button variant="ghost" size="sm" onClick={() => handleGenerateCertificate(app.user_id)} title="Generate Certificate">
                            <Award className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Final status indicators */}
                        {app.status === "secretariat_accepted" && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleSendPaymentLinkR3(app)}
                            className="text-teal-600"
                            title="Send Payment Link"
                          >
                            <CreditCard className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {app.status === "secretariat_rejected" && (
                          <span className="text-xs px-2 text-red-600">Rejected</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Correction Request Dialog */}
      <Dialog open={correctionDialog.open} onOpenChange={(open) => !open && setCorrectionDialog({ open: false, app: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Application Correction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-600 mb-2">
                Applicant: <span className="font-medium">{correctionDialog.app?.user_name || correctionDialog.app?.user_email}</span>
              </p>
              <Label className="text-sm font-medium">Correction Details</Label>
              <Textarea
                value={correctionText}
                onChange={(e) => setCorrectionText(e.target.value)}
                placeholder="Please describe what corrections or additional information is needed..."
                rows={5}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                This message will be emailed to the applicant
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionDialog({ open: false, app: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestCorrection} 
              disabled={sendingCorrection || !correctionText.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {sendingCorrection ? "Sending..." : "Send Correction Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Details Modal */}
      <Dialog open={detailsModal.open} onOpenChange={(open) => !open && setDetailsModal({ open: false, app: null, documents: [] })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Application Details</DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : detailsModal.app && (
            <div className="space-y-6">
              {/* Applicant Header */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center">
                  <Users className="w-7 h-7 text-teal-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{detailsModal.user?.name || detailsModal.app.user_name || 'Applicant'}</h3>
                  <p className="text-slate-600">{detailsModal.user?.email || detailsModal.app.user_email}</p>
                  <p className="text-sm text-slate-500">{detailsModal.user?.phone || detailsModal.app.user_phone}</p>
                </div>
                {getStatusBadge(detailsModal.app.status)}
              </div>

              {/* Application Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <Label className="text-slate-500">Application ID</Label>
                  <p className="font-mono">{detailsModal.app.application_id}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Membership Type</Label>
                  <p className="capitalize">{detailsModal.app.membership_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Submitted</Label>
                  <p>{detailsModal.app.submission_date ? new Date(detailsModal.app.submission_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <p className="capitalize">{detailsModal.app.status?.replace(/_/g, ' ')}</p>
                </div>
              </div>

              {/* Release 3: Independent Status Fields - Aligned Layout */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-500 mb-1">Application Decision</span>
                    <Badge className={`w-fit ${detailsModal.app.application_decision === 'secretariat_accepted' ? 'bg-emerald-100 text-emerald-800' : detailsModal.app.application_decision === 'secretariat_rejected' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>
                      {detailsModal.app.application_decision?.replace(/_/g, ' ') || 'Pending'}
                    </Badge>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-500 mb-1">Payment Status</span>
                    <Badge className={`w-fit ${detailsModal.app.payment_status === 'payment_received' ? 'bg-emerald-100 text-emerald-800' : detailsModal.app.payment_status === 'payment_failed' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>
                      {detailsModal.app.payment_status?.replace(/_/g, ' ') || 'N/A'}
                    </Badge>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-500 mb-1">Certificate Status</span>
                    <Badge className={`w-fit ${detailsModal.app.certificate_status === 'certificate_sent' ? 'bg-teal-100 text-teal-800' : detailsModal.app.certificate_status === 'certificate_approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                      {detailsModal.app.certificate_status?.replace(/_/g, ' ') || 'N/A'}
                    </Badge>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-500 mb-1">Membership Status</span>
                    <Badge className={`w-fit ${detailsModal.app.membership_status === 'active' ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-700'}`}>
                      {detailsModal.app.membership_status?.replace(/_/g, ' ') || 'Pending'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Personal Details */}
              {detailsModal.app.personal_details && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">Personal Details</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {Object.entries(detailsModal.app.personal_details).map(([key, value]) => (
                        value && (
                          <div key={key}>
                            <Label className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</Label>
                            <p>{String(value)}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Professional Details */}
              {detailsModal.app.professional_details && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">Professional Details</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {Object.entries(detailsModal.app.professional_details).map(([key, value]) => (
                        value && (
                          <div key={key}>
                            <Label className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</Label>
                            <p>{String(value)}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Uploaded Documents */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base">Uploaded Documents ({detailsModal.documents?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {!detailsModal.documents || detailsModal.documents.length === 0 ? (
                    <p className="text-slate-500 text-sm">No documents uploaded</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {detailsModal.documents.map((doc) => (
                        <div key={doc.file_id} className="border rounded-lg p-3 bg-slate-50">
                          <p className="font-medium text-sm capitalize">{doc.type?.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-slate-500 truncate">{doc.filename}</p>
                          <div className="flex gap-2 mt-1">
                            <button 
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/secure/file/${doc.file_id}/view`, {
                                    withCredentials: true,
                                    responseType: 'blob'
                                  });
                                  const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                                  const url = window.URL.createObjectURL(blob);
                                  window.open(url, '_blank');
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Access denied or file not found");
                                }
                              }}
                              className="text-xs text-blue-700 hover:underline flex items-center gap-1"
                              data-testid={`view-doc-${doc.file_id}`}
                            >
                              <Eye className="w-3 h-3" /> View
                            </button>
                            <button 
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/secure/file/${doc.file_id}/download`, {
                                    withCredentials: true,
                                    responseType: 'blob'
                                  });
                                  const blob = new Blob([response.data]);
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', doc.filename || 'document');
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Access denied or file not found");
                                }
                              }}
                              className="text-xs text-teal-700 hover:underline flex items-center gap-1"
                              data-testid={`download-doc-${doc.file_id}`}
                            >
                              <Download className="w-3 h-3" /> Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Correction History */}
              {detailsModal.corrections && detailsModal.corrections.length > 0 && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">Correction Requests</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {detailsModal.corrections.map((c, idx) => (
                        <div key={idx} className="border-l-2 border-amber-400 pl-3 py-2 bg-amber-50 rounded-r">
                          <p className="text-sm">{c.correction_comments}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {c.created_at ? new Date(c.created_at).toLocaleString() : ''} - Status: {c.status}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Payment Info */}
              {detailsModal.payment && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">Payment Information</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <Label className="text-slate-500">Amount</Label>
                        <p>₹{detailsModal.payment.amount}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Status</Label>
                        <Badge>{detailsModal.payment.status}</Badge>
                      </div>
                      <div>
                        <Label className="text-slate-500">Order ID</Label>
                        <p className="font-mono text-xs">{detailsModal.payment.razorpay_order_id || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Payment ID</Label>
                        <p className="font-mono text-xs">{detailsModal.payment.razorpay_payment_id || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Release 2: Application Timeline */}
              {detailsModal.timeline && detailsModal.timeline.length > 0 && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <History className="w-4 h-4" /> Application Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {detailsModal.timeline.map((event, idx) => (
                        <div key={idx} className="flex gap-3 text-sm border-l-2 border-slate-200 pl-3">
                          <div className="flex-1">
                            <p className="font-medium capitalize">{event.action?.replace(/_/g, ' ')}</p>
                            {event.old_status && event.new_status && (
                              <p className="text-xs text-slate-500">
                                {event.old_status?.replace(/_/g, ' ')} → {event.new_status?.replace(/_/g, ' ')}
                              </p>
                            )}
                            {event.comments && <p className="text-xs text-slate-600 mt-1">{event.comments}</p>}
                            <p className="text-xs text-slate-400 mt-1">
                              {event.timestamp ? new Date(event.timestamp).toLocaleString() : ''} 
                              {event.actor && ` • ${event.actor}`}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs h-fit">
                            {event.event_type}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Release 2: Secretariat Review Modal */}
      <Dialog open={secretariatReviewModal.open} onOpenChange={(open) => !open && setSecretariatReviewModal({ open: false, app: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Secretariat Completeness Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Applicant: <span className="font-medium">{secretariatReviewModal.app?.user_name || secretariatReviewModal.app?.user_email}</span>
            </p>
            
            {Object.entries(checklist).map(([section, items]) => (
              <Card key={section}>
                <CardHeader className="py-2">
                  <CardTitle className="text-sm capitalize">{section.replace(/_/g, ' ')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {Object.entries(items).map(([itemId, data]) => (
                      <div key={itemId} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm capitalize">{itemId.replace(/_/g, ' ')}</span>
                          <Select value={data.status} onValueChange={(v) => setChecklist(prev => ({
                            ...prev,
                            [section]: { ...prev[section], [itemId]: { ...prev[section][itemId], status: v } }
                          }))}>
                            <SelectTrigger className="w-44 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="complete">Complete</SelectItem>
                              <SelectItem value="incomplete">Incomplete</SelectItem>
                              <SelectItem value="clarification_needed">Needs Clarification</SelectItem>
                              <SelectItem value="not_applicable">Not Applicable</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {data.status === "not_applicable" && (
                          <Input 
                            placeholder="Reason for N/A (required)" 
                            value={data.na_reason || ""}
                            onChange={(e) => setChecklist(prev => ({
                              ...prev,
                              [section]: { ...prev[section], [itemId]: { ...prev[section][itemId], na_reason: e.target.value } }
                            }))}
                            className="h-8 text-sm border-amber-200"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            
            <div>
              <Label>Internal Notes (not visible to applicant)</Label>
              <Textarea 
                value={internalNotes} 
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Add any internal notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSecretariatReviewModal({ open: false, app: null })}>
              Cancel
            </Button>
            <Button variant="ghost" onClick={handleSaveChecklistOnly} disabled={processing}>
              <Save className="w-4 h-4 mr-1" />
              Save Checklist
            </Button>
            <Button variant="secondary" onClick={() => handleSecretariatReview(false)} disabled={processing}>
              Save Review
            </Button>
            <Button onClick={() => handleSecretariatReview(true)} disabled={processing} className="bg-teal-700 hover:bg-teal-800">
              Forward to Committee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release 2: Structured Correction Modal */}
      <Dialog open={structuredCorrectionModal.open} onOpenChange={(open) => !open && setStructuredCorrectionModal({ open: false, app: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Corrections</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Applicant: <span className="font-medium">{structuredCorrectionModal.app?.user_name || structuredCorrectionModal.app?.user_email}</span>
            </p>
            
            <div className="space-y-3">
              {correctionItems.map((item, idx) => (
                <Card key={idx} className="p-3">
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Field or Document name"
                        value={item.field_or_document}
                        onChange={(e) => updateCorrectionItem(idx, 'field_or_document', e.target.value)}
                        className="flex-1"
                      />
                      <label className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          checked={item.is_mandatory}
                          onChange={(e) => updateCorrectionItem(idx, 'is_mandatory', e.target.checked)}
                        />
                        Required
                      </label>
                      <Button variant="ghost" size="sm" onClick={() => removeCorrectionItem(idx)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <Textarea
                      placeholder="Specific comment for this item..."
                      value={item.comment}
                      onChange={(e) => updateCorrectionItem(idx, 'comment', e.target.value)}
                      rows={2}
                    />
                  </div>
                </Card>
              ))}
              <Button variant="outline" size="sm" onClick={addCorrectionItem}>
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            
            <div>
              <Label>Additional Comments</Label>
              <Textarea 
                value={additionalComments} 
                onChange={(e) => setAdditionalComments(e.target.value)}
                placeholder="Any additional instructions..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStructuredCorrectionModal({ open: false, app: null })}>
              Cancel
            </Button>
            <Button onClick={handleStructuredCorrection} disabled={processing} className="bg-amber-600 hover:bg-amber-700">
              {processing ? "Sending..." : "Send Correction Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release 2: Committee Assignment Modal */}
      <Dialog open={committeeAssignModal.open} onOpenChange={(open) => !open && setCommitteeAssignModal({ open: false, app: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Committee Reviewers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Applicant: <span className="font-medium">{committeeAssignModal.app?.user_name || committeeAssignModal.app?.user_email}</span>
            </p>
            
            <div>
              <Label>Select Reviewers</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                {availableReviewers.length === 0 ? (
                  <p className="text-sm text-slate-500">No reviewers available. Add committee members first.</p>
                ) : availableReviewers.map((r) => (
                  <label key={r.user_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded">
                    <input
                      type="checkbox"
                      checked={selectedReviewers.includes(r.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedReviewers([...selectedReviewers, r.user_id]);
                        } else {
                          setSelectedReviewers(selectedReviewers.filter(id => id !== r.user_id));
                        }
                      }}
                    />
                    <span className="text-sm">{r.name}</span>
                    <span className="text-xs text-slate-500">({r.email})</span>
                    {r.committee && <Badge variant="outline" className="text-xs">{r.committee}</Badge>}
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Review Link Expires In</Label>
              <Select value={String(expiryHours)} onValueChange={(v) => setExpiryHours(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                  <SelectItem value="72">72 hours</SelectItem>
                  <SelectItem value="168">7 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitteeAssignModal({ open: false, app: null })}>
              Cancel
            </Button>
            <Button onClick={handleAssignCommittee} disabled={processing || selectedReviewers.length === 0} className="bg-indigo-600 hover:bg-indigo-700">
              {processing ? "Assigning..." : `Assign ${selectedReviewers.length} Reviewer(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release 2: Final Decision Modal */}
      <Dialog open={finalDecisionModal.open} onOpenChange={(open) => !open && setFinalDecisionModal({ open: false, app: null, committeeDecisions: [] })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Secretariat Final Decision</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Applicant: <span className="font-medium">{finalDecisionModal.app?.user_name || finalDecisionModal.app?.user_email}</span>
            </p>
            
            {/* Committee Decisions Summary */}
            {finalDecisionModal.committeeDecisions.length > 0 && (
              <Card>
                <CardHeader className="py-2">
                  <CardTitle className="text-sm">Committee Reviews</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {finalDecisionModal.committeeDecisions.map((d, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm p-2 bg-slate-50 rounded">
                        <Badge className={
                          d.decision === 'recommend' ? 'bg-emerald-100 text-emerald-800' :
                          d.decision === 'hold' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-rose-100 text-rose-800'
                        }>
                          {d.decision}
                        </Badge>
                        <div className="flex-1">
                          <p className="font-medium">{d.reviewer_name}</p>
                          {d.comments && <p className="text-slate-600 text-xs">{d.comments}</p>}
                          <p className="text-xs text-slate-400">{new Date(d.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            <div>
              <Label>Your Decision</Label>
              <Select value={finalDecision.decision} onValueChange={(v) => setFinalDecision(prev => ({ ...prev, decision: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select decision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accept">Accept Application</SelectItem>
                  <SelectItem value="reject">Reject Application</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Comments {finalDecision.decision === 'reject' && <span className="text-red-500">*</span>}</Label>
              <Textarea 
                value={finalDecision.comments} 
                onChange={(e) => setFinalDecision(prev => ({ ...prev, comments: e.target.value }))}
                placeholder="Add comments..."
                rows={3}
              />
              {finalDecision.decision === 'reject' && (
                <p className="text-xs text-slate-500 mt-1">Rejection reason will be emailed to the applicant.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalDecisionModal({ open: false, app: null, committeeDecisions: [] })}>
              Cancel
            </Button>
            <Button 
              onClick={handleFinalDecision} 
              disabled={processing || !finalDecision.decision || (finalDecision.decision === 'reject' && !finalDecision.comments)}
              className={finalDecision.decision === 'accept' ? 'bg-teal-700 hover:bg-teal-800' : 'bg-red-600 hover:bg-red-700'}
            >
              {processing ? "Processing..." : finalDecision.decision === 'accept' ? 'Accept Application' : 'Reject Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// UAT Enhancement: Secretariat Application Master View with 11 Quick Filters
const ApplicationMasterView = () => {
  const navigate = require('react-router-dom').useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCounts, setFilterCounts] = useState({});
  const [quickFilter, setQuickFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("submission_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 25;
  
  // Details modal state
  const [detailsModal, setDetailsModal] = useState({ open: false, app: null });
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [quickFilter, sortBy, sortOrder, page]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/applications-master-enhanced?quick_filter=${quickFilter}&sort_by=${sortBy}&sort_order=${sortOrder}&page=${page}&page_size=${pageSize}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setApplications(response.data.applications || []);
      setTotal(response.data.total || 0);
      setFilterCounts(response.data.filter_counts || {});
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchApplications();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const openApplicationDetail = async (app) => {
    setLoadingDetails(true);
    setDetailsModal({ open: true, app });
    try {
      const response = await axios.get(`${API}/admin/applications/${app.application_id}/details`, { withCredentials: true });
      setDetailsModal({ open: true, app: response.data.application || app, documents: response.data.documents || [] });
    } catch (error) {
      console.error("Error loading details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Quick filter definitions with labels
  const quickFilters = [
    { key: "all", label: "All Applications", icon: FileText },
    { key: "awaiting_secretariat", label: "Awaiting Secretariat Review", icon: Clock },
    { key: "incomplete_clarification", label: "Incomplete / Needs Clarification", icon: AlertTriangle },
    { key: "ready_for_committee", label: "Ready for Review Committee", icon: UserPlus },
    { key: "under_committee", label: "Under Review Committee", icon: Users },
    { key: "recommendation_received", label: "Committee Recommendation Received", icon: MessageSquare },
    { key: "pending_secretariat_decision", label: "Pending Secretariat Decision", icon: ClipboardList },
    { key: "payment_pending", label: "Payment Pending", icon: CreditCard },
    { key: "certificate_processing", label: "Certificate Processing", icon: Award },
    { key: "active", label: "Active", icon: CheckCircle },
    { key: "rejected", label: "Rejected", icon: X }
  ];

  // Status badge styling
  const getStatusBadge = (status, value) => {
    if (!value || value === "—" || value === "pending") {
      return <span className="text-slate-400 text-xs">—</span>;
    }
    
    const configs = {
      // Current stage
      "Submitted": "bg-blue-100 text-blue-800",
      "Secretariat Review": "bg-purple-100 text-purple-800",
      "Resubmitted": "bg-cyan-100 text-cyan-800",
      "Correction Requested": "bg-amber-100 text-amber-800",
      "Clarification Needed": "bg-amber-100 text-amber-800",
      "Ready for Committee": "bg-indigo-100 text-indigo-800",
      "Under Committee": "bg-violet-100 text-violet-800",
      "Recommendation Received": "bg-emerald-100 text-emerald-800",
      "On Hold": "bg-yellow-100 text-yellow-800",
      "Committee Rejected": "bg-rose-100 text-rose-800",
      "Accepted": "bg-teal-100 text-teal-800",
      "Rejected": "bg-red-100 text-red-800",
      "Payment Pending": "bg-orange-100 text-orange-800",
      "Payment Received": "bg-blue-100 text-blue-800",
      "Active": "bg-teal-100 text-teal-800",
      // Application decision
      "secretariat_accepted": "bg-teal-100 text-teal-800",
      "secretariat_rejected": "bg-red-100 text-red-800",
      // Payment status
      "payment_pending": "bg-orange-100 text-orange-800",
      "payment_received": "bg-emerald-100 text-emerald-800",
      "payment_failed": "bg-red-100 text-red-800",
      "payment_refunded": "bg-slate-100 text-slate-800",
      // Certificate status
      "certificate_pending_review": "bg-yellow-100 text-yellow-800",
      "certificate_approved": "bg-emerald-100 text-emerald-800",
      "certificate_sent": "bg-teal-100 text-teal-800",
      "certificate_rejected": "bg-red-100 text-red-800",
      // Membership status
      "active": "bg-teal-100 text-teal-800",
      "pending_activation": "bg-yellow-100 text-yellow-800",
      "inactive": "bg-slate-100 text-slate-800",
      "seeded": "bg-blue-100 text-blue-800",
      // Completeness
      "Complete": "bg-emerald-100 text-emerald-800",
      "Not reviewed": "bg-slate-100 text-slate-600"
    };
    
    const colorClass = configs[value] || "bg-slate-100 text-slate-700";
    const displayValue = value.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    
    return (
      <Badge className={`${colorClass} text-xs font-medium whitespace-nowrap`}>
        {displayValue}
      </Badge>
    );
  };

  const renderSortIcon = (field) => (
    <>
      <ArrowUpDown className={`w-3 h-3 ${sortBy === field ? 'text-slate-900' : 'text-slate-400'}`} />
      {sortBy === field && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
    </>
  );

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6 min-w-0" data-testid="application-master-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading font-bold text-2xl text-slate-900">Application Master View</h2>
        <Button variant="outline" size="sm" onClick={fetchApplications} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* P3-01 Fix: 11 Quick Filters with proper overflow handling */}
      <div className="overflow-x-auto -mx-1 px-1 pb-2" data-testid="quick-filters">
        <div className="flex flex-wrap gap-2 min-w-0">
          {quickFilters.map(({ key, label, icon: Icon }) => (
            <Button
              key={key}
              variant={quickFilter === key ? "default" : "outline"}
              size="sm"
              onClick={() => { setQuickFilter(key); setPage(1); }}
              className={`text-xs shrink-0 ${quickFilter === key ? 'bg-slate-900 text-white' : ''}`}
              data-testid={`filter-${key}`}
            >
              <Icon className="w-3 h-3 mr-1 shrink-0" />
              <span className="truncate max-w-[150px]">{label}</span>
              <Badge className={`ml-1 shrink-0 ${quickFilter === key ? 'bg-white/20 text-white' : 'bg-slate-100'}`}>
                {filterCounts[key] ?? 0}
              </Badge>
            </Button>
          ))}
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by Reference, Name, or Type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Button type="submit" variant="outline">Search</Button>
        {search && (
          <Button type="button" variant="ghost" onClick={() => { setSearch(""); setPage(1); fetchApplications(); }}>
            Clear
          </Button>
        )}
      </form>

      {/* Results count */}
      <p className="text-sm text-slate-600">
        Showing {applications.length > 0 ? ((page - 1) * pageSize + 1) : 0}–{Math.min(page * pageSize, total)} of {total} applications
      </p>

      {/* P3-01 Fix: Applications Table with proper scroll containment */}
      <Card className="border border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-w-full">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="empty-state">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No applications found</p>
                <p className="text-sm">Try adjusting your filters or search criteria</p>
              </div>
            ) : (
              <Table className="min-w-[1200px]">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="whitespace-nowrap cursor-pointer hover:bg-slate-100" onClick={() => handleSort("application_id")}>
                    <div className="flex items-center gap-1">Ref {renderSortIcon("application_id")}</div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap cursor-pointer hover:bg-slate-100" onClick={() => handleSort("name")}>
                    <div className="flex items-center gap-1">Name {renderSortIcon("name")}</div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap cursor-pointer hover:bg-slate-100" onClick={() => handleSort("membership_type")}>
                    <div className="flex items-center gap-1">Type {renderSortIcon("membership_type")}</div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap cursor-pointer hover:bg-slate-100" onClick={() => handleSort("submission_date")}>
                    <div className="flex items-center gap-1">Submitted {renderSortIcon("submission_date")}</div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Current Stage</TableHead>
                  <TableHead className="whitespace-nowrap">Completeness</TableHead>
                  <TableHead className="whitespace-nowrap hidden lg:table-cell">Committee / Reviewer</TableHead>
                  <TableHead className="whitespace-nowrap hidden lg:table-cell">Committee Rec</TableHead>
                  <TableHead className="whitespace-nowrap hidden xl:table-cell">Secretariat Action</TableHead>
                  <TableHead className="whitespace-nowrap hidden xl:table-cell">Payment</TableHead>
                  <TableHead className="whitespace-nowrap hidden xl:table-cell">Certificate</TableHead>
                  <TableHead className="whitespace-nowrap">Membership</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.application_id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-xs">
                      <button 
                        onClick={() => openApplicationDetail(app)}
                        className="text-blue-600 hover:underline"
                        data-testid={`app-ref-${app.application_id}`}
                      >
                        {app.application_id?.slice(-8) || "—"}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button 
                        onClick={() => openApplicationDetail(app)}
                        className="text-left hover:text-blue-600"
                      >
                        <p className="font-medium text-sm">{app.user_name || app.personal_details?.full_name || "—"}</p>
                        <p className="text-xs text-slate-500">{app.user_email || "—"}</p>
                      </button>
                    </TableCell>
                    <TableCell className="capitalize text-sm">
                      {app.membership_type?.replace(/_/g, " ") || "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {app.submission_date ? new Date(app.submission_date).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>{getStatusBadge("stage", app.current_stage)}</TableCell>
                    <TableCell>{getStatusBadge("completeness", app.completeness_status)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs">
                      {app.assigned_committee && app.assigned_committee !== "—" ? (
                        <div>
                          <p className="font-medium">{app.assigned_committee}</p>
                          {app.assigned_reviewers && app.assigned_reviewers !== "—" && (
                            <p className="text-slate-500">{app.assigned_reviewers}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {app.committee_recommendation && app.committee_recommendation !== "—" 
                        ? getStatusBadge("rec", app.committee_recommendation)
                        : <span className="text-slate-400 text-xs">—</span>
                      }
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {getStatusBadge("decision", app.application_decision)}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {getStatusBadge("payment", app.payment_status)}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {getStatusBadge("cert", app.certificate_status)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge("membership", app.membership_status)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Application Details Modal */}
      <Dialog open={detailsModal.open} onOpenChange={(open) => !open && setDetailsModal({ open: false, app: null })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
            </div>
          ) : detailsModal.app && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <Label className="text-slate-500">Reference</Label>
                  <p className="font-mono">{detailsModal.app.application_id}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Applicant</Label>
                  <p>{detailsModal.app.user_name || detailsModal.app.personal_details?.full_name || "—"}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Type</Label>
                  <p className="capitalize">{detailsModal.app.membership_type?.replace(/_/g, " ")}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Submitted</Label>
                  <p>{detailsModal.app.submission_date ? new Date(detailsModal.app.submission_date).toLocaleDateString() : "—"}</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Current Stage</Label>
                  {getStatusBadge("stage", detailsModal.app.current_stage)}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Application Decision</Label>
                  {getStatusBadge("decision", detailsModal.app.application_decision)}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Payment Status</Label>
                  {getStatusBadge("payment", detailsModal.app.payment_status)}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Membership Status</Label>
                  {getStatusBadge("membership", detailsModal.app.membership_status)}
                </div>
              </div>
              
              {detailsModal.app.next_actions && detailsModal.app.next_actions.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Available Actions</Label>
                    <div className="flex flex-wrap gap-2">
                      {detailsModal.app.next_actions.includes("review") && (
                        <Button size="sm" variant="outline" onClick={() => { setDetailsModal({ open: false, app: null }); navigate('/admin/applications'); }}>
                          <ClipboardList className="w-4 h-4 mr-1" /> Review Application
                        </Button>
                      )}
                      {detailsModal.app.next_actions.includes("assign_committee") && (
                        <Button size="sm" variant="outline" onClick={() => { setDetailsModal({ open: false, app: null }); navigate('/admin/applications'); }}>
                          <UserPlus className="w-4 h-4 mr-1" /> Assign Committee
                        </Button>
                      )}
                      {detailsModal.app.next_actions.includes("final_decision") && (
                        <Button size="sm" variant="outline" onClick={() => { setDetailsModal({ open: false, app: null }); navigate('/admin/applications'); }}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Make Decision
                        </Button>
                      )}
                      {detailsModal.app.next_actions.includes("send_payment_link") && (
                        <Button size="sm" variant="outline" onClick={() => { setDetailsModal({ open: false, app: null }); navigate('/admin/applications'); }}>
                          <CreditCard className="w-4 h-4 mr-1" /> Send Payment Link
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};



// Directory Management (Export/Import)
const DirectoryManagement = () => {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importLogs, setImportLogs] = useState([]);
  const [showErrors, setShowErrors] = useState(null);
  const [exportFilters, setExportFilters] = useState({
    status: "all", membership_type: "all", city: "", format: "xlsx"
  });
  const [specialities, setSpecialities] = useState([]);

  useEffect(() => {
    fetchImportLogs();
    fetchSpecialities();
  }, []);

  const fetchImportLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/import/logs`, { withCredentials: true });
      setImportLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch import logs");
    }
  };

  const fetchSpecialities = async () => {
    try {
      const response = await axios.get(`${API}/specialities`);
      setSpecialities(response.data || []);
    } catch (error) {
      console.error("Failed to fetch specialities");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/import/template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cpsi_member_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (exportFilters.status) params.append("status", exportFilters.status);
      if (exportFilters.membership_type) params.append("membership_type", exportFilters.membership_type);
      if (exportFilters.city) params.append("city", exportFilters.city);
      params.append("format", exportFilters.format);

      const response = await axios.get(`${API}/admin/export/members?${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const ext = exportFilters.format === "csv" ? "csv" : "xlsx";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cpsi_members_export_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completed");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'csv'].includes(ext)) {
      toast.error("File must be .xlsx or .csv");
      return;
    }

    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/import/members`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      fetchImportLogs();
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} members imported successfully`);
      }
      if (response.data.failed_count > 0) {
        toast.error(`${response.data.failed_count} rows failed validation`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDownloadErrors = async (batchId) => {
    try {
      const response = await axios.get(`${API}/admin/import/errors/${batchId}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `import_errors_${batchId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download error report");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Member Import/Export</h1>
        <p className="text-slate-500 text-sm">Bulk import existing members or export member data</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Import Section */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-700" />
              Import Members
            </CardTitle>
            <CardDescription>
              Upload Excel (.xlsx) or CSV file with member data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" onClick={handleDownloadTemplate} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Download Import Template
            </Button>
            
            <Separator />
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Upload File</Label>
              <Input
                type="file"
                accept=".xlsx,.csv"
                onChange={handleImport}
                disabled={importing}
              />
              <p className="text-xs text-slate-500 mt-1">Accepts .xlsx and .csv files</p>
            </div>

            {importing && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Processing file...
              </div>
            )}

            {importResult && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-sm">Import Summary</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <div className="text-lg font-bold">{importResult.total_rows}</div>
                    <div className="text-xs text-slate-500">Total Rows</div>
                  </div>
                  <div className="text-center p-2 bg-green-50 rounded">
                    <div className="text-lg font-bold text-green-700">{importResult.success_count}</div>
                    <div className="text-xs text-green-600">Success</div>
                  </div>
                  <div className="text-center p-2 bg-red-50 rounded">
                    <div className="text-lg font-bold text-red-700">{importResult.failed_count}</div>
                    <div className="text-xs text-red-600">Failed</div>
                  </div>
                </div>
                
                {importResult.errors?.length > 0 && (
                  <div className="mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowErrors(importResult.errors)}
                      className="w-full"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Errors ({importResult.errors.length})
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Section */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Download className="w-5 h-5 text-teal-700" />
              Export Members
            </CardTitle>
            <CardDescription>
              Download member data with optional filters
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={exportFilters.status} onValueChange={(v) => setExportFilters(p => ({...p, status: v}))}>
                  <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Membership Type</Label>
                <Select value={exportFilters.membership_type} onValueChange={(v) => setExportFilters(p => ({...p, membership_type: v}))}>
                  <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="fellow">Fellow</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="associate">Associate</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input 
                  placeholder="Filter by city" 
                  value={exportFilters.city}
                  onChange={(e) => setExportFilters(p => ({...p, city: e.target.value}))}
                />
              </div>
              <div>
                <Label className="text-xs">Format</Label>
                <Select value={exportFilters.format} onValueChange={(v) => setExportFilters(p => ({...p, format: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                    <SelectItem value="csv">CSV (.csv)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button onClick={handleExport} className="w-full bg-slate-900 text-white">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Members
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Import History */}
      {importLogs.length > 0 && (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Import History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Success</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importLogs.slice(0, 10).map((log) => (
                  <TableRow key={log.batch_id}>
                    <TableCell className="text-xs">
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{log.file_name}</TableCell>
                    <TableCell className="text-xs">{log.admin_email}</TableCell>
                    <TableCell>{log.total_rows}</TableCell>
                    <TableCell className="text-green-700">{log.success_count}</TableCell>
                    <TableCell className="text-red-700">{log.failed_count}</TableCell>
                    <TableCell>
                      {log.failed_count > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownloadErrors(log.batch_id)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Error Details Modal */}
      <Dialog open={!!showErrors} onOpenChange={() => setShowErrors(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Validation Errors</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Row</TableHead>
                <TableHead className="w-32">Column</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showErrors?.map((err, idx) => (
                <TableRow key={idx}>
                  <TableCell>{err.row}</TableCell>
                  <TableCell className="font-mono text-xs">{err.column}</TableCell>
                  <TableCell className="text-sm text-red-600">{err.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Certificate Management
const CertificateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [configModal, setConfigModal] = useState(false);
  const [fieldConfig, setFieldConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [editName, setEditName] = useState("");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/certificate-templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, ""));

    try {
      await axios.post(`${API}/admin/certificate-template`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Template uploaded and activated");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to upload template");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const openConfig = async (template) => {
    try {
      const response = await axios.get(`${API}/admin/certificate-templates/${template.template_id}`, { withCredentials: true });
      setSelectedTemplate(response.data);
      setEditName(response.data.name || "");
      setFieldConfig(response.data.field_config || {
        member_name: { x: 300, y: 350, font_size: 28, font_color: "#000000" },
        membership_type: { x: 300, y: 400, font_size: 18, font_color: "#333333" },
        membership_number: { x: 300, y: 430, font_size: 14, font_color: "#666666" },
        issue_date: { x: 300, y: 460, font_size: 14, font_color: "#666666" },
        certificate_reference: { x: 300, y: 490, font_size: 12, font_color: "#999999" },
        signer1_name: { x: 150, y: 600, font_size: 14, font_color: "#000000", value: "" },
        signer1_designation: { x: 150, y: 620, font_size: 12, font_color: "#666666", value: "" },
        signer2_name: { x: 450, y: 600, font_size: 14, font_color: "#000000", value: "" },
        signer2_designation: { x: 450, y: 620, font_size: 12, font_color: "#666666", value: "" }
      });
      setConfigModal(true);
    } catch (error) {
      toast.error("Failed to load template details");
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/certificate-templates/${selectedTemplate.template_id}/config`, 
        { field_config: fieldConfig },
        { withCredentials: true }
      );
      toast.success("Configuration saved");
      setConfigModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, prop, value) => {
    setFieldConfig(prev => ({
      ...prev,
      [field]: { ...prev[field], [prop]: value }
    }));
  };

  const handleActivate = async (templateId) => {
    try {
      await axios.post(`${API}/admin/certificate-templates/${templateId}/activate`, {}, { withCredentials: true });
      toast.success("Template activated - it will be used for new certificates");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to activate template");
    }
  };

  const handleDeactivate = async (templateId) => {
    try {
      await axios.post(`${API}/admin/certificate-templates/${templateId}/deactivate`, {}, { withCredentials: true });
      toast.success("Template deactivated");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to deactivate template");
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await axios.get(`${API}/admin/certificate-templates/${template.template_id}/preview`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success("Preview opened - showing sample certificate data");
    } catch (error) {
      toast.error("Failed to generate preview");
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim() || !selectedTemplate) return;
    try {
      await axios.put(`${API}/admin/certificate-templates/${selectedTemplate.template_id}/name`, 
        { name: editName.trim() },
        { withCredentials: true }
      );
      toast.success("Template name updated");
      setSelectedTemplate(prev => ({ ...prev, name: editName.trim() }));
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to update name");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-slate-900">Certificate Templates</h2>
        <Button variant="outline" onClick={fetchTemplates}>
          <RefreshCw className={`w-4 h-4 mr-2 ${uploading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Workflow Guide */}
      <Card className="border border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Template Workflow</h3>
          <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
            <li><strong>Upload</strong> a blank PDF/image template (your certificate background)</li>
            <li><strong>Configure</strong> field positions and signer details</li>
            <li><strong>Preview</strong> to verify layout with sample data</li>
            <li><strong>Activate</strong> to use for new member certificates</li>
          </ol>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Upload className="w-5 h-5 text-teal-700" />
            Upload New Template
          </CardTitle>
          <CardDescription>
            Upload a blank PDF or image to use as certificate background. The system overlays member details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleUpload}
              disabled={uploading}
              className="cursor-pointer flex-1"
              data-testid="template-file-input"
            />
            {uploading && <span className="text-sm text-slate-500">Uploading...</span>}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Supported: PDF, PNG, JPEG. Max 10MB. New uploads auto-activate.
          </p>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Uploaded Templates</CardTitle>
          <CardDescription>Manage templates - only one can be active at a time</CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Award className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No templates uploaded yet.</p>
              <p className="text-sm">A default CPSI template will be used for certificates.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.template_id} data-testid={`template-row-${template.template_id}`}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{template.filename}</TableCell>
                    <TableCell>
                      {template.is_active ? (
                        <Badge className="bg-teal-100 text-teal-800">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-800">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{new Date(template.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handlePreview(template)}
                          title="Preview with sample data"
                          data-testid={`preview-template-${template.template_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openConfig(template)}
                          title="Configure fields"
                          data-testid={`configure-template-${template.template_id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        {template.is_active ? (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeactivate(template.template_id)}
                            className="text-amber-600"
                            title="Deactivate template"
                            data-testid={`deactivate-template-${template.template_id}`}
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleActivate(template.template_id)}
                            className="text-teal-600"
                            title="Activate template"
                            data-testid={`activate-template-${template.template_id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Configuration Modal */}
      <Dialog open={configModal} onOpenChange={setConfigModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Template: {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Template Name Edit */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label>Template Name</Label>
                <Input 
                  value={editName} 
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Template name"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={handleSaveName}
                disabled={!editName.trim() || editName === selectedTemplate?.name}
              >
                Rename
              </Button>
            </div>
            
            <Separator />
            
            <p className="text-sm text-slate-600">Configure X/Y positions (in points from bottom-left) and font sizes for certificate fields.</p>
            
            {/* Dynamic Fields */}
            <div className="grid gap-4">
              <Card>
                <CardHeader className="py-2"><CardTitle className="text-sm">Member Details (Auto-filled from profile)</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  {["member_name", "membership_type", "membership_number", "issue_date", "certificate_reference"].map(field => (
                    <div key={field} className="space-y-1">
                      <Label className="capitalize">{field.replace(/_/g, " ")}</Label>
                      <div className="flex gap-2">
                        <Input type="number" placeholder="X" value={fieldConfig[field]?.x || 0} onChange={(e) => updateField(field, "x", parseInt(e.target.value))} className="w-20" />
                        <Input type="number" placeholder="Y" value={fieldConfig[field]?.y || 0} onChange={(e) => updateField(field, "y", parseInt(e.target.value))} className="w-20" />
                        <Input type="number" placeholder="Size" value={fieldConfig[field]?.font_size || 14} onChange={(e) => updateField(field, "font_size", parseInt(e.target.value))} className="w-20" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="py-2"><CardTitle className="text-sm">Signer 1 (e.g., President)</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input value={fieldConfig.signer1_name?.value || ""} onChange={(e) => updateField("signer1_name", "value", e.target.value)} placeholder="Dr. John Smith" />
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Input value={fieldConfig.signer1_designation?.value || ""} onChange={(e) => updateField("signer1_designation", "value", e.target.value)} placeholder="President, CPSI" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input type="number" placeholder="X" value={fieldConfig.signer1_name?.x || 0} onChange={(e) => updateField("signer1_name", "x", parseInt(e.target.value))} className="w-24" />
                    <Input type="number" placeholder="Y" value={fieldConfig.signer1_name?.y || 0} onChange={(e) => updateField("signer1_name", "y", parseInt(e.target.value))} className="w-24" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="py-2"><CardTitle className="text-sm">Signer 2 (e.g., Secretary)</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input value={fieldConfig.signer2_name?.value || ""} onChange={(e) => updateField("signer2_name", "value", e.target.value)} placeholder="Dr. Jane Doe" />
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Input value={fieldConfig.signer2_designation?.value || ""} onChange={(e) => updateField("signer2_designation", "value", e.target.value)} placeholder="General Secretary, CPSI" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input type="number" placeholder="X" value={fieldConfig.signer2_name?.x || 0} onChange={(e) => updateField("signer2_name", "x", parseInt(e.target.value))} className="w-24" />
                    <Input type="number" placeholder="Y" value={fieldConfig.signer2_name?.y || 0} onChange={(e) => updateField("signer2_name", "y", parseInt(e.target.value))} className="w-24" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfigModal(false)}>Cancel</Button>
            <Button variant="secondary" onClick={() => handlePreview(selectedTemplate)}>
              <Eye className="w-4 h-4 mr-2" /> Preview
            </Button>
            <Button onClick={saveConfig} disabled={saving} className="bg-teal-700 hover:bg-teal-800">
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Integration Settings
const IntegrationSettings = () => {
  const [razorpay, setRazorpay] = useState({ key_id: '', key_secret: '', webhook_secret: '', enabled: false });
  const [email, setEmail] = useState({ provider: 'zeptomail', api_key: '', from_email: 'noreply@cpsindia.org.in', enabled: false });
  const [whatsapp, setWhatsapp] = useState({ provider: 'zoho', api_key: '', phone_number_id: '', enabled: false });
  const [saving, setSaving] = useState({ razorpay: false, email: false, whatsapp: false });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [razorpayRes, emailRes, whatsappRes] = await Promise.all([
        axios.get(`${API}/admin/settings/razorpay`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/email`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/whatsapp`, { withCredentials: true })
      ]);
      setRazorpay(razorpayRes.data);
      setEmail(emailRes.data);
      setWhatsapp(whatsappRes.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const saveRazorpay = async () => {
    setSaving(prev => ({ ...prev, razorpay: true }));
    try {
      await axios.post(`${API}/admin/settings/razorpay`, razorpay, { withCredentials: true });
      toast.success("Razorpay settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(prev => ({ ...prev, razorpay: false }));
    }
  };

  const saveEmail = async () => {
    setSaving(prev => ({ ...prev, email: true }));
    try {
      await axios.post(`${API}/admin/settings/email`, email, { withCredentials: true });
      toast.success("Email settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(prev => ({ ...prev, email: false }));
    }
  };

  const saveWhatsapp = async () => {
    setSaving(prev => ({ ...prev, whatsapp: true }));
    try {
      await axios.post(`${API}/admin/settings/whatsapp`, whatsapp, { withCredentials: true });
      toast.success("WhatsApp settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(prev => ({ ...prev, whatsapp: false }));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading font-bold text-2xl text-slate-900">Integration Settings</h2>

      <Tabs defaultValue="razorpay" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="razorpay" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Razorpay
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            WhatsApp
          </TabsTrigger>
        </TabsList>

        {/* Razorpay Settings */}
        <TabsContent value="razorpay">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Razorpay Payment Gateway</CardTitle>
              <CardDescription>Configure Razorpay for membership payments</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Enable Razorpay</Label>
                <Switch
                  checked={razorpay.enabled}
                  onCheckedChange={(checked) => setRazorpay(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Key ID</Label>
                  <Input
                    value={razorpay.key_id}
                    onChange={(e) => setRazorpay(prev => ({ ...prev, key_id: e.target.value }))}
                    placeholder="rzp_live_xxxxx or rzp_test_xxxxx"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Key Secret</Label>
                  <Input
                    type="password"
                    value={razorpay.key_secret}
                    onChange={(e) => setRazorpay(prev => ({ ...prev, key_secret: e.target.value }))}
                    placeholder="Enter key secret"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Webhook Secret (Optional)</Label>
                  <Input
                    type="password"
                    value={razorpay.webhook_secret || ''}
                    onChange={(e) => setRazorpay(prev => ({ ...prev, webhook_secret: e.target.value }))}
                    placeholder="For webhook verification"
                  />
                </div>
              </div>
              <Button onClick={saveRazorpay} disabled={saving.razorpay} className="bg-slate-900 text-white">
                {saving.razorpay ? "Saving..." : "Save Settings"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Email Service (ZeptoMail/Zoho)</CardTitle>
              <CardDescription>Configure email for notifications and verification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Enable Email Service</Label>
                <Switch
                  checked={email.enabled}
                  onCheckedChange={(checked) => setEmail(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Provider</Label>
                  <Select
                    value={email.provider}
                    onValueChange={(value) => setEmail(prev => ({ ...prev, provider: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zeptomail">ZeptoMail</SelectItem>
                      <SelectItem value="zohomail">Zoho Mail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">API Key</Label>
                  <Input
                    type="password"
                    value={email.api_key || ''}
                    onChange={(e) => setEmail(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="Enter API key"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">From Email</Label>
                  <Input
                    value={email.from_email}
                    onChange={(e) => setEmail(prev => ({ ...prev, from_email: e.target.value }))}
                    placeholder="noreply@cpsindia.org.in"
                  />
                </div>
              </div>
              <Button onClick={saveEmail} disabled={saving.email} className="bg-slate-900 text-white">
                {saving.email ? "Saving..." : "Save Settings"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Settings */}
        <TabsContent value="whatsapp">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">WhatsApp OTP Service</CardTitle>
              <CardDescription>Configure WhatsApp for phone verification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Enable WhatsApp OTP</Label>
                <Switch
                  checked={whatsapp.enabled}
                  onCheckedChange={(checked) => setWhatsapp(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Provider</Label>
                  <Select
                    value={whatsapp.provider}
                    onValueChange={(value) => setWhatsapp(prev => ({ ...prev, provider: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zoho">Zoho</SelectItem>
                      <SelectItem value="twilio">Twilio</SelectItem>
                      <SelectItem value="gupshup">Gupshup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">API Key</Label>
                  <Input
                    type="password"
                    value={whatsapp.api_key || ''}
                    onChange={(e) => setWhatsapp(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="Enter API key"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Phone Number ID</Label>
                  <Input
                    value={whatsapp.phone_number_id || ''}
                    onChange={(e) => setWhatsapp(prev => ({ ...prev, phone_number_id: e.target.value }))}
                    placeholder="WhatsApp Business Phone ID"
                  />
                </div>
              </div>
              <Button onClick={saveWhatsapp} disabled={saving.whatsapp} className="bg-slate-900 text-white">
                {saving.whatsapp ? "Saving..." : "Save Settings"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Release 3: Certificate Review and Approval
const CertificatesReview = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedCert, setSelectedCert] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [actionComments, setActionComments] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchCertificates();
  }, [statusFilter]);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      const response = await axios.get(`${API}/admin/certificates?${params}`, { withCredentials: true });
      setCertificates(response.data.certificates || []);
    } catch (error) {
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (certId, action) => {
    if (action === "reject" && !actionComments.trim()) {
      toast.error("Please provide rejection comments");
      return;
    }
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/certificates/${certId}/action`, {
        action,
        comments: actionComments
      }, { withCredentials: true });
      toast.success(`Certificate ${action}${action === "approve" ? "d" : "ed"} successfully`);
      setDetailsModal(false);
      setActionComments("");
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} certificate`);
    } finally {
      setProcessing(false);
    }
  };

  const handleSend = async (certId) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/certificates/${certId}/send`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchCertificates();
      setDetailsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send certificate");
    } finally {
      setProcessing(false);
    }
  };

  const viewDetails = async (cert) => {
    try {
      const response = await axios.get(`${API}/admin/certificates/${cert.certificate_id}`, { withCredentials: true });
      setSelectedCert(response.data);
      setDetailsModal(true);
      setActionComments("");
    } catch (error) {
      toast.error("Failed to load certificate details");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: "bg-slate-100 text-slate-800", label: "Draft" },
      approved: { color: "bg-emerald-100 text-emerald-800", label: "Approved" },
      rejected: { color: "bg-red-100 text-red-800", label: "Rejected" },
      sent: { color: "bg-teal-100 text-teal-800", label: "Sent" }
    };
    const c = config[status] || { color: "bg-slate-100 text-slate-800", label: status };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Certificate Management</h1>
          <p className="text-slate-500 text-sm">Review and send membership certificates</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Certificates</SelectItem>
              <SelectItem value="draft">Pending Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchCertificates}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
            </div>
          ) : certificates.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No certificates found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Membership #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certificates.map((cert) => (
                  <TableRow key={cert.certificate_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{cert.applicant?.name || "—"}</p>
                        <p className="text-sm text-slate-500">{cert.applicant?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{cert.membership_number}</TableCell>
                    <TableCell className="capitalize">{cert.membership_type?.replace("_", " ")}</TableCell>
                    <TableCell>{getStatusBadge(cert.status)}</TableCell>
                    <TableCell>{cert.generated_at ? new Date(cert.generated_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewDetails(cert)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {cert.status === "draft" && (
                          <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleAction(cert.certificate_id, "approve")}>
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {cert.status === "approved" && (
                          <Button variant="ghost" size="sm" className="text-teal-600" onClick={() => handleSend(cert.certificate_id)}>
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Certificate Details Modal */}
      <Dialog open={detailsModal} onOpenChange={setDetailsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Certificate Details</DialogTitle>
          </DialogHeader>
          {selectedCert && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Member Name</Label>
                  <p className="font-medium">{selectedCert.applicant?.name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Email</Label>
                  <p>{selectedCert.applicant?.email}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Membership Number</Label>
                  <p className="font-mono">{selectedCert.certificate?.membership_number}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Type</Label>
                  <p className="capitalize">{selectedCert.certificate?.membership_type?.replace("_", " ")}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  {getStatusBadge(selectedCert.certificate?.status)}
                </div>
                <div>
                  <Label className="text-slate-500">Generated</Label>
                  <p>{selectedCert.certificate?.generated_at ? new Date(selectedCert.certificate.generated_at).toLocaleString() : "—"}</p>
                </div>
              </div>

              {selectedCert.certificate?.status === "draft" && (
                <div className="border-t pt-4">
                  <Label>Review Comments (optional for approve, required for reject)</Label>
                  <Textarea
                    value={actionComments}
                    onChange={(e) => setActionComments(e.target.value)}
                    placeholder="Add comments..."
                    rows={3}
                    className="mt-2"
                  />
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => handleAction(selectedCert.certificate.certificate_id, "approve")}
                      disabled={processing}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {processing ? "Processing..." : "Approve Certificate"}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleAction(selectedCert.certificate.certificate_id, "reject")}
                      disabled={processing || !actionComments.trim()}
                    >
                      Reject Certificate
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleAction(selectedCert.certificate.certificate_id, "regenerate")}
                      disabled={processing}
                    >
                      Regenerate
                    </Button>
                  </div>
                </div>
              )}

              {selectedCert.certificate?.status === "approved" && (
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-600 mb-3">Certificate approved. Ready to send to member.</p>
                  <Button
                    onClick={() => handleSend(selectedCert.certificate.certificate_id)}
                    disabled={processing}
                    className="bg-teal-700 hover:bg-teal-800"
                  >
                    {processing ? "Sending..." : "Send Certificate & Activate Membership"}
                  </Button>
                </div>
              )}

              {selectedCert.certificate?.status === "sent" && (
                <div className="border-t pt-4 text-sm text-slate-600">
                  <p>Certificate sent on {selectedCert.certificate?.sent_at ? new Date(selectedCert.certificate.sent_at).toLocaleString() : "—"}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Specialities Management
const SpecialitiesManagement = () => {
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newSpeciality, setNewSpeciality] = useState({ name: "", description: "" });

  useEffect(() => {
    fetchSpecialities();
  }, []);

  const fetchSpecialities = async () => {
    try {
      const response = await axios.get(`${API}/admin/specialities`);
      setSpecialities(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSpeciality = async () => {
    try {
      await axios.post(`${API}/admin/specialities`, newSpeciality, { withCredentials: true });
      setModalOpen(false);
      setNewSpeciality({ name: "", description: "" });
      fetchSpecialities();
      toast.success("Speciality added");
    } catch (error) {
      toast.error("Error adding speciality");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-slate-900">Specialities</h2>
        <Button onClick={() => setModalOpen(true)} className="bg-slate-900 text-white hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" />
          Add Speciality
        </Button>
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {specialities.map((spec) => (
                <TableRow key={spec.id}>
                  <TableCell className="font-medium">{spec.name}</TableCell>
                  <TableCell className="text-slate-500">{spec.description || "-"}</TableCell>
                  <TableCell>
                    <Badge className={spec.active ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-800"}>
                      {spec.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm"><Edit className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Speciality</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Name</Label>
              <Input
                value={newSpeciality.name}
                onChange={(e) => setNewSpeciality({ ...newSpeciality, name: e.target.value })}
                placeholder="e.g., Clinical Psychology"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Description</Label>
              <Input
                value={newSpeciality.description}
                onChange={(e) => setNewSpeciality({ ...newSpeciality, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSpeciality}>Add Speciality</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Payments Management
const PaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, [filter]);

  const fetchPayments = async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const response = await axios.get(`${API}/admin/payments${params}`, { withCredentials: true });
      setPayments(response.data.payments || []);
    } catch (error) {
      toast.error("Failed to fetch payments");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
      completed: { color: "bg-green-100 text-green-800", label: "Completed" },
      failed: { color: "bg-red-100 text-red-800", label: "Failed" },
      expired: { color: "bg-slate-100 text-slate-800", label: "Expired" },
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Payments</h1>
          <p className="text-slate-500 text-sm">Payment records and audit trail</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchPayments}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Application</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.order_id}>
                <TableCell className="font-mono text-xs">{payment.order_id}</TableCell>
                <TableCell className="font-mono text-xs">{payment.application_id || "-"}</TableCell>
                <TableCell>₹{payment.amount}</TableCell>
                <TableCell>{getStatusBadge(payment.status)}</TableCell>
                <TableCell className="text-xs">{formatDate(payment.created_at)}</TableCell>
                <TableCell className="text-xs">{formatDate(payment.completed_at)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedPayment(payment)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  No payments found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Payment Details Modal */}
      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-slate-500">Order ID</Label>
                  <p className="font-mono">{selectedPayment.order_id}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Razorpay Order</Label>
                  <p className="font-mono">{selectedPayment.razorpay_order_id || "-"}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Razorpay Payment ID</Label>
                  <p className="font-mono">{selectedPayment.razorpay_payment_id || "-"}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <p>{getStatusBadge(selectedPayment.status)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Amount</Label>
                  <p>₹{selectedPayment.amount} {selectedPayment.currency}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Verified Via</Label>
                  <p>{selectedPayment.verified_via || "-"}</p>
                </div>
                {selectedPayment.error_code && (
                  <>
                    <div>
                      <Label className="text-slate-500">Error Code</Label>
                      <p className="text-red-600">{selectedPayment.error_code}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Error Description</Label>
                      <p className="text-red-600">{selectedPayment.error_description}</p>
                    </div>
                  </>
                )}
              </div>
              
              {selectedPayment.event_log?.length > 0 && (
                <div>
                  <Label className="text-slate-500 mb-2 block">Event Log</Label>
                  <div className="bg-slate-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {selectedPayment.event_log.map((event, idx) => (
                      <div key={idx} className="text-xs border-b border-slate-200 py-2 last:border-0">
                        <span className="font-medium">{event.event}</span>
                        <span className="text-slate-400 ml-2">{formatDate(event.timestamp)}</span>
                        {event.source && <span className="text-slate-400 ml-2">via {event.source}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Email Logs Management
const EmailLogsManagement = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");

  useEffect(() => {
    fetchEmails();
  }, [filter, templateFilter]);

  const fetchEmails = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);
      if (templateFilter !== "all") params.append("template", templateFilter);
      
      const response = await axios.get(`${API}/admin/emails?${params}`, { withCredentials: true });
      setEmails(response.data.emails || []);
    } catch (error) {
      toast.error("Failed to fetch email logs");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
      sent: { color: "bg-green-100 text-green-800", label: "Sent" },
      failed: { color: "bg-red-100 text-red-800", label: "Failed" },
      test_mode: { color: "bg-blue-100 text-blue-800", label: "Test Mode" },
      error: { color: "bg-red-100 text-red-800", label: "Error" },
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Email Logs</h1>
          <p className="text-slate-500 text-sm">Transactional email delivery history</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="test_mode">Test Mode</SelectItem>
            </SelectContent>
          </Select>
          <Select value={templateFilter} onValueChange={setTemplateFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Templates</SelectItem>
              <SelectItem value="otp_verification">OTP Verification</SelectItem>
              <SelectItem value="registration_confirmation">Registration</SelectItem>
              <SelectItem value="application_submitted">Application Submitted</SelectItem>
              <SelectItem value="application_approved">Approved</SelectItem>
              <SelectItem value="payment_link">Payment Link</SelectItem>
              <SelectItem value="payment_success">Payment Success</SelectItem>
              <SelectItem value="password_reset">Password Reset</SelectItem>
              <SelectItem value="membership_activated">Membership Activated</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchEmails}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {emails.map((email) => (
              <TableRow key={email.email_id}>
                <TableCell className="text-xs">{formatDate(email.created_at)}</TableCell>
                <TableCell className="text-xs font-mono">{email.to_email}</TableCell>
                <TableCell className="text-xs">{email.template?.replace(/_/g, " ")}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate">{email.subject}</TableCell>
                <TableCell>{getStatusBadge(email.status)}</TableCell>
              </TableRow>
            ))}
            {emails.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                  No email logs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Seeded Members Management
const SeededMembersManagement = () => {
  const [members, setMembers] = useState([]);
  const [counts, setCounts] = useState({ seeded: 0, reviewed: 0, active: 0, deleted: 0 });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchSeededMembers();
  }, [statusFilter]);

  const fetchSeededMembers = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status_filter=${statusFilter}` : "";
      const response = await axios.get(`${API}/admin/seeded-members${params}`, { withCredentials: true });
      setMembers(response.data.members || []);
      setCounts(response.data.counts || { seeded: 0, reviewed: 0, active: 0, deleted: 0 });
    } catch (error) {
      toast.error("Failed to fetch members");
    } finally {
      setLoading(false);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/directory/seed-import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      toast.success(`Imported ${response.data.imported} members`);
      fetchSeededMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error("No members selected");
      return;
    }
    
    const actionLabels = { review: "mark as reviewed", activate: "activate", delete: "delete", restore: "restore", invite: "send invitations to" };
    if (action !== "review" && !confirm(`${actionLabels[action]} ${selectedIds.length} selected members?`)) return;
    
    setBulkActionLoading(true);
    try {
      if (action === "invite") {
        // Release 3: Send invitation emails
        const response = await axios.post(`${API}/admin/seeded-members/invite`, 
          { user_ids: selectedIds },
          { withCredentials: true }
        );
        toast.success(`Sent ${response.data.invited_count} invitations`);
      } else {
        const response = await axios.post(`${API}/admin/seeded-members/bulk-action`, 
          { user_ids: selectedIds, action },
          { withCredentials: true }
        );
        toast.success(response.data.message);
      }
      setSelectedIds([]);
      fetchSeededMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(members.map(m => m.user_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (userId, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, userId]);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== userId));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      seeded: { label: "Seeded", className: "bg-slate-100 text-slate-700" },
      reviewed: { label: "Reviewed", className: "bg-blue-100 text-blue-700" },
      active: { label: "Active", className: "bg-teal-100 text-teal-700" },
      deleted: { label: "Deleted", className: "bg-red-100 text-red-700" }
    };
    const c = config[status] || { label: status, className: "bg-slate-100" };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="seeded-members-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Seeded Members</h1>
          <p className="text-slate-600">Review imported seed members before making them public</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSeededMembers} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleImportCSV}
              className="hidden"
              disabled={importing}
            />
            <Button asChild disabled={importing}>
              <span>
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Importing..." : "Import CSV"}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Status Counts */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { key: "seeded", label: "Seeded", color: "bg-slate-100" },
          { key: "reviewed", label: "Reviewed", color: "bg-blue-100" },
          { key: "active", label: "Active", color: "bg-teal-100" },
          { key: "deleted", label: "Deleted", color: "bg-red-100" }
        ].map(s => (
          <Card 
            key={s.key} 
            className={`cursor-pointer transition-all ${statusFilter === s.key ? 'ring-2 ring-slate-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === s.key ? "all" : s.key)}
          >
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{counts[s.key] || 0}</div>
              <div className="text-sm text-slate-600">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {importResult && (
        <Card className="bg-teal-50 border-teal-200">
          <CardContent className="pt-4">
            <p className="font-medium text-teal-800">{importResult.message}</p>
            {importResult.errors?.length > 0 && (
              <div className="mt-2 text-sm text-amber-700">
                <p className="font-medium">Warnings:</p>
                <ul className="list-disc pl-5">
                  {importResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <Card className="bg-slate-50 border-slate-300">
          <CardContent className="py-3 flex items-center justify-between">
            <span className="text-sm font-medium">{selectedIds.length} selected</span>
            <div className="flex gap-2">
              {statusFilter !== "reviewed" && statusFilter !== "active" && (
                <Button size="sm" variant="outline" onClick={() => handleBulkAction("review")} disabled={bulkActionLoading}>
                  <Eye className="w-4 h-4 mr-1" /> Mark Reviewed
                </Button>
              )}
              {/* Release 3: Send Invitation Emails */}
              {(statusFilter === "seeded" || statusFilter === "reviewed") && (
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => handleBulkAction("invite")} disabled={bulkActionLoading}>
                  <Mail className="w-4 h-4 mr-1" /> Send Invitations
                </Button>
              )}
              {statusFilter !== "active" && (
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => handleBulkAction("activate")} disabled={bulkActionLoading}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Activate
                </Button>
              )}
              {statusFilter === "deleted" ? (
                <Button size="sm" variant="outline" onClick={() => handleBulkAction("restore")} disabled={bulkActionLoading}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Restore
                </Button>
              ) : statusFilter !== "active" && (
                <Button size="sm" variant="destructive" onClick={() => handleBulkAction("delete")} disabled={bulkActionLoading}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Clear</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {statusFilter === "all" ? "All Imported Members" : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Members`} 
            ({members.length})
          </CardTitle>
          <CardDescription>
            {statusFilter === "active" 
              ? "These members are visible in the public directory" 
              : "These members are NOT visible in the public directory"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading...</p>
          ) : members.length === 0 ? (
            <p className="text-center py-8 text-slate-500">
              {statusFilter === "all" ? "No imported members. Import a CSV to get started." : `No ${statusFilter} members.`}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input 
                      type="checkbox" 
                      checked={selectedIds.length === members.length && members.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded"
                    />
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Membership #</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Approval Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.user_id} className={selectedIds.includes(member.user_id) ? "bg-slate-50" : ""}>
                    <TableCell>
                      <input 
                        type="checkbox" 
                        checked={selectedIds.includes(member.user_id)}
                        onChange={(e) => handleSelectOne(member.user_id, e.target.checked)}
                        className="rounded"
                      />
                    </TableCell>
                    <TableCell>{getStatusBadge(member.status)}</TableCell>
                    <TableCell className="font-mono text-sm">{member.membership_number}</TableCell>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell className="text-sm">{member.email}</TableCell>
                    <TableCell className="text-sm">{member.phone}</TableCell>
                    <TableCell>{member.city}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {member.membership_type?.replace('Membership', '').trim()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {member.approval_date ? new Date(member.approval_date).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Deleted Members Management - Phase 2 Enhanced Hard Delete
const DeletedMembersManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [typedConfirmation, setTypedConfirmation] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchDeletedMembers();
  }, []);

  const fetchDeletedMembers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/deleted-members`, { withCredentials: true });
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error("Failed to fetch deleted members");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (userId) => {
    try {
      await axios.post(`${API}/admin/deleted-members/${userId}/restore`, {}, { withCredentials: true });
      toast.success("Member restored");
      fetchDeletedMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Restore failed");
    }
  };

  const fetchDeletePreview = async (member) => {
    try {
      const response = await axios.get(`${API}/admin/members/${member.user_id}/hard-delete-preview`, { withCredentials: true });
      setDeletePreview(response.data);
      setConfirmDelete(member);
      setTypedConfirmation("");
      setDeleteReason("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch deletion preview");
    }
  };

  const handlePermanentDelete = async () => {
    if (!deleteReason.trim()) {
      toast.error("Deletion reason is required");
      return;
    }
    if (typedConfirmation.toUpperCase() !== deletePreview?.confirmation_required) {
      toast.error(`Type exactly: ${deletePreview?.confirmation_required}`);
      return;
    }
    
    setIsDeleting(true);
    try {
      await axios.post(`${API}/admin/members/${confirmDelete.user_id}/hard-delete`, {
        typed_confirmation: typedConfirmation,
        reason: deleteReason
      }, { withCredentials: true });
      toast.success("Member permanently deleted");
      setConfirmDelete(null);
      setDeletePreview(null);
      setTypedConfirmation("");
      setDeleteReason("");
      fetchDeletedMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="deleted-members-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Deleted Members</h1>
          <p className="text-slate-600">Soft-deleted members can be restored or permanently removed</p>
        </div>
        <Button variant="outline" onClick={fetchDeletedMembers} data-testid="refresh-deleted-members">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading...</p>
          ) : members.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No deleted members</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Original Status</TableHead>
                  <TableHead>Deleted At</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.user_id}>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{member.original_status || 'unknown'}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {member.deleted_at ? new Date(member.deleted_at).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{member.deletion_reason || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleRestore(member.user_id)} title="Restore" data-testid={`restore-member-${member.user_id}`}>
                          <RotateCcw className="w-4 h-4 text-teal-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => fetchDeletePreview(member)} title="Permanently Delete" data-testid={`hard-delete-member-${member.user_id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Permanent Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => { setConfirmDelete(null); setDeletePreview(null); setTypedConfirmation(""); setDeleteReason(""); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Permanently Delete Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-semibold text-red-800">Warning: This action is IRREVERSIBLE</p>
              <p className="text-sm text-red-700 mt-1">All personal data will be permanently removed.</p>
            </div>

            <div className="text-sm">
              <p className="font-medium text-slate-700">Member: <span className="text-slate-900">{deletePreview?.name}</span></p>
              <p className="text-slate-600">{deletePreview?.email}</p>
              {deletePreview?.has_active_membership && (
                <Badge variant="destructive" className="mt-1">Has Active Membership</Badge>
              )}
            </div>

            {deletePreview?.impact_summary && (
              <div className="border rounded-lg p-3 space-y-3 text-sm">
                <div>
                  <p className="font-medium text-red-700 mb-1">Will be Deleted:</p>
                  <ul className="text-slate-600 space-y-0.5 ml-4 list-disc">
                    <li>User login access & sessions</li>
                    <li>Personal documents: {deletePreview.impact_summary.will_be_deleted.personal_documents}</li>
                    <li>Profile personal data</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-amber-700 mb-1">Will be Anonymized:</p>
                  <ul className="text-slate-600 space-y-0.5 ml-4 list-disc">
                    <li>Member profile (kept for records)</li>
                    <li>Payment records: {deletePreview.impact_summary.will_be_anonymized.payment_records}</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-teal-700 mb-1">Will be Preserved (Audit):</p>
                  <ul className="text-slate-600 space-y-0.5 ml-4 list-disc">
                    <li>Payment transaction history</li>
                    <li>Review decisions made: {deletePreview.impact_summary.will_be_preserved.review_decisions_made}</li>
                    <li>Deletion audit record</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="delete-reason">Reason for Deletion *</Label>
              <Textarea
                id="delete-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Enter reason for permanent deletion..."
                className="min-h-[60px]"
                data-testid="hard-delete-reason"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="typed-confirmation">Type to confirm: <code className="bg-slate-100 px-1 rounded text-red-600">{deletePreview?.confirmation_required}</code></Label>
              <Input
                id="typed-confirmation"
                value={typedConfirmation}
                onChange={(e) => setTypedConfirmation(e.target.value)}
                placeholder={deletePreview?.confirmation_required}
                className="font-mono"
                data-testid="hard-delete-confirmation-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmDelete(null); setDeletePreview(null); }} data-testid="cancel-hard-delete">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handlePermanentDelete}
              disabled={isDeleting || typedConfirmation.toUpperCase() !== deletePreview?.confirmation_required || !deleteReason.trim()}
              data-testid="confirm-hard-delete"
            >
              {isDeleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Committees Management
const CommitteesManagement = () => {
  const [committees, setCommittees] = useState([]);
  const [inactiveCommittees, setInactiveCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCommittee, setShowAddCommittee] = useState(false);
  const [showAddMember, setShowAddMember] = useState(null);
  const [editCommittee, setEditCommittee] = useState(null);
  const [showDeactivate, setShowDeactivate] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showDuplicateReport, setShowDuplicateReport] = useState(false);
  const [duplicateReport, setDuplicateReport] = useState([]);
  const [newCommittee, setNewCommittee] = useState({ name: '', description: '' });
  const [newMember, setNewMember] = useState({ name: '', role: '', email: '', phone: '', whatsapp: '' });
  const [expandedCommittee, setExpandedCommittee] = useState(null);
  const [editMember, setEditMember] = useState(null);

  useEffect(() => {
    fetchCommittees();
  }, []);

  const fetchCommittees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/committees`, { withCredentials: true });
      setCommittees(response.data.committees || []);
      // Fetch inactive committees
      const inactiveRes = await axios.get(`${API}/admin/committees/inactive`, { withCredentials: true });
      setInactiveCommittees(inactiveRes.data.committees || []);
    } catch (error) {
      toast.error("Failed to fetch committees");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommittee = async () => {
    if (!newCommittee.name.trim()) {
      toast.error("Committee name is required");
      return;
    }
    try {
      await axios.post(`${API}/admin/committees`, newCommittee, { withCredentials: true });
      toast.success("Committee created");
      setShowAddCommittee(false);
      setNewCommittee({ name: '', description: '' });
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create committee");
    }
  };

  const handleEditCommittee = async () => {
    if (!editCommittee.name.trim()) {
      toast.error("Committee name is required");
      return;
    }
    try {
      await axios.put(`${API}/admin/committees/${editCommittee.committee_id}`, {
        name: editCommittee.name,
        description: editCommittee.description
      }, { withCredentials: true });
      toast.success("Committee updated");
      setEditCommittee(null);
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update committee");
    }
  };

  const handleDeactivate = async () => {
    try {
      await axios.delete(`${API}/admin/committees/${showDeactivate.committee_id}`, { withCredentials: true });
      toast.success("Committee deactivated");
      setShowDeactivate(null);
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate committee");
    }
  };

  const handleReactivate = async (committeeId) => {
    try {
      await axios.post(`${API}/admin/committees/${committeeId}/reactivate`, {}, { withCredentials: true });
      toast.success("Committee reactivated");
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate committee");
    }
  };

  const handleDeletePermanent = async () => {
    if (deleteConfirmation.toLowerCase() !== showDelete.name.toLowerCase()) {
      toast.error("Please type the committee name exactly to confirm");
      return;
    }
    try {
      await axios.delete(`${API}/admin/committees/${showDelete.committee_id}/permanent`, { withCredentials: true });
      toast.success("Committee permanently deleted");
      setShowDelete(null);
      setDeleteConfirmation("");
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete committee");
    }
  };

  const fetchDuplicateReport = async () => {
    try {
      const response = await axios.get(`${API}/admin/committees/duplicate-report`, { withCredentials: true });
      setDuplicateReport(response.data.duplicates || []);
      setShowDuplicateReport(true);
    } catch (error) {
      toast.error("Failed to fetch duplicate report");
    }
  };

  const handleAddMember = async (committeeId) => {
    if (!newMember.name.trim() || !newMember.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/committees/${committeeId}/members`, 
        { ...newMember, committee_id: committeeId, active: true },
        { withCredentials: true }
      );
      toast.success("Member added");
      setShowAddMember(null);
      setNewMember({ name: '', role: '', email: '', phone: '', whatsapp: '' });
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    }
  };

  const handleUpdateMember = async () => {
    try {
      await axios.put(`${API}/admin/committee-members/${editMember.member_id}`, editMember, { withCredentials: true });
      toast.success("Member updated");
      setEditMember(null);
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update member");
    }
  };

  const handleToggleMemberActive = async (member) => {
    try {
      await axios.put(`${API}/admin/committee-members/${member.member_id}`, 
        { ...member, active: !member.active },
        { withCredentials: true }
      );
      toast.success(member.active ? "Member deactivated" : "Member reactivated");
      fetchCommittees();
    } catch (error) {
      toast.error("Failed to update member status");
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(`${API}/admin/committee-members/${memberId}`, { withCredentials: true });
      toast.success("Member removed");
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  return (
    <div className="space-y-6" data-testid="committees-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Executive Committee & Other Committees</h1>
          <p className="text-slate-600">Manage CPSI committees and their members</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDuplicateReport} data-testid="duplicate-report-btn">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Duplicate Check
          </Button>
          <Button variant="outline" onClick={() => setShowInactive(!showInactive)}>
            {showInactive ? "Hide Inactive" : "Show Inactive"}
          </Button>
          <Button onClick={() => setShowAddCommittee(true)} data-testid="add-committee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Committee
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-8 text-slate-500">Loading...</p>
      ) : committees.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">
            No committees created yet. Click &quot;Add Committee&quot; to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {committees.map((committee) => (
            <Card key={committee.committee_id}>
              <CardHeader 
                className="cursor-pointer"
                onClick={() => setExpandedCommittee(expandedCommittee === committee.committee_id ? null : committee.committee_id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {committee.name}
                      <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                    </CardTitle>
                    {committee.description && (
                      <CardDescription>{committee.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{committee.members?.filter(m => m.active !== false).length || 0} active members</Badge>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => setEditCommittee(committee)} title="Edit/Rename" data-testid={`edit-committee-${committee.committee_id}`}>
                        <Pencil className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowDeactivate(committee)} title="Deactivate" data-testid={`deactivate-committee-${committee.committee_id}`}>
                        <Power className="w-4 h-4 text-amber-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowDelete(committee)} title="Delete" data-testid={`delete-committee-${committee.committee_id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    {expandedCommittee === committee.committee_id ? 
                      <ChevronUp className="w-5 h-5" /> : 
                      <ChevronDown className="w-5 h-5" />
                    }
                  </div>
                </div>
              </CardHeader>
              {expandedCommittee === committee.committee_id && (
                <CardContent>
                  <div className="flex justify-end mb-4">
                    <Button size="sm" variant="outline" onClick={() => setShowAddMember(committee.committee_id)}>
                      <Plus className="w-4 h-4 mr-1" /> Add Member
                    </Button>
                  </div>
                  {committee.members?.length === 0 ? (
                    <p className="text-center py-4 text-slate-500">No members in this committee</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>WhatsApp</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {committee.members?.map((member) => (
                          <TableRow key={member.member_id} className={member.active === false ? "opacity-50" : ""}>
                            <TableCell className="font-medium">{member.name}</TableCell>
                            <TableCell>{member.role}</TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>{member.phone || '-'}</TableCell>
                            <TableCell>{member.whatsapp || '-'}</TableCell>
                            <TableCell>
                              <Badge className={member.active !== false ? 'bg-teal-100 text-teal-700' : 'bg-slate-100'}>
                                {member.active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button variant="ghost" size="sm" onClick={() => setEditMember({...member})} title="Edit">
                                  <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleToggleMemberActive(member)} title={member.active !== false ? "Deactivate" : "Reactivate"}>
                                  <Power className={`w-4 h-4 ${member.active !== false ? 'text-amber-500' : 'text-green-500'}`} />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(member.member_id)} title="Remove">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Inactive Committees Section */}
      {showInactive && inactiveCommittees.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading font-bold text-lg text-slate-700 mt-8">Inactive/Deactivated Committees</h2>
          {inactiveCommittees.map((committee) => (
            <Card key={committee.committee_id} className="border-slate-300 bg-slate-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2 text-slate-600">
                      {committee.name}
                      <Badge className="bg-slate-200 text-slate-600 text-xs">Inactive</Badge>
                    </CardTitle>
                    {committee.description && (
                      <CardDescription>{committee.description}</CardDescription>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleReactivate(committee.committee_id)}>
                    Reactivate
                  </Button>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Add Committee Dialog */}
      <Dialog open={showAddCommittee} onOpenChange={setShowAddCommittee}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Committee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Committee Name *</Label>
              <Input 
                value={newCommittee.name} 
                onChange={(e) => setNewCommittee({...newCommittee, name: e.target.value})}
                placeholder="e.g., Executive Committee"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={newCommittee.description} 
                onChange={(e) => setNewCommittee({...newCommittee, description: e.target.value})}
                placeholder="Brief description of the committee"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCommittee(false)}>Cancel</Button>
            <Button onClick={handleCreateCommittee}>Create Committee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={!!showAddMember} onOpenChange={() => setShowAddMember(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Committee Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={newMember.name} onChange={(e) => setNewMember({...newMember, name: e.target.value})} />
              </div>
              <div>
                <Label>Role/Designation *</Label>
                <Input value={newMember.role} onChange={(e) => setNewMember({...newMember, role: e.target.value})} placeholder="Chairperson, Member, etc." />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newMember.email} onChange={(e) => setNewMember({...newMember, email: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={newMember.phone} onChange={(e) => setNewMember({...newMember, phone: e.target.value})} />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={newMember.whatsapp} onChange={(e) => setNewMember({...newMember, whatsapp: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(null)}>Cancel</Button>
            <Button onClick={() => handleAddMember(showAddMember)}>Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Committee Dialog */}
      <Dialog open={!!editCommittee} onOpenChange={() => setEditCommittee(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Committee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Committee Name *</Label>
              <Input 
                value={editCommittee?.name || ''} 
                onChange={(e) => setEditCommittee({...editCommittee, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={editCommittee?.description || ''} 
                onChange={(e) => setEditCommittee({...editCommittee, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCommittee(null)}>Cancel</Button>
            <Button onClick={handleEditCommittee}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Committee Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={editMember?.name || ''} onChange={(e) => setEditMember({...editMember, name: e.target.value})} />
              </div>
              <div>
                <Label>Role/Designation</Label>
                <Input value={editMember?.role || ''} onChange={(e) => setEditMember({...editMember, role: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={editMember?.email || ''} onChange={(e) => setEditMember({...editMember, email: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={editMember?.phone || ''} onChange={(e) => setEditMember({...editMember, phone: e.target.value})} />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={editMember?.whatsapp || ''} onChange={(e) => setEditMember({...editMember, whatsapp: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMember(null)}>Cancel</Button>
            <Button onClick={handleUpdateMember}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Committee Dialog */}
      <Dialog open={!!showDeactivate} onOpenChange={() => setShowDeactivate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Power className="w-5 h-5" />
              Deactivate Committee
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p>Are you sure you want to deactivate <strong>{showDeactivate?.name}</strong>?</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p><strong>What happens:</strong></p>
              <ul className="list-disc ml-4 mt-1 space-y-1">
                <li>Committee is removed from active lists and assignment options</li>
                <li>Historical assignments, reviews, and decisions are preserved</li>
                <li>You can reactivate this committee later</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivate(null)}>Cancel</Button>
            <Button variant="default" className="bg-amber-600 hover:bg-amber-700" onClick={handleDeactivate}>Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Committee Dialog */}
      <Dialog open={!!showDelete} onOpenChange={() => { setShowDelete(null); setDeleteConfirmation(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Committee
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {showDelete?.has_history ? (
              <div className="bg-slate-50 border rounded-lg p-4 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="font-medium">This committee has historical assignments or decisions</p>
                <p className="text-sm text-slate-600 mt-1">It can only be deactivated, not deleted.</p>
              </div>
            ) : (
              <>
                <p>Are you sure you want to permanently delete <strong>{showDelete?.name}</strong>?</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  <p><strong>Warning:</strong> This action is irreversible.</p>
                </div>
                <div>
                  <Label>Type the committee name to confirm: <code className="bg-slate-100 px-1">{showDelete?.name}</code></Label>
                  <Input 
                    value={deleteConfirmation} 
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="Type committee name"
                    className="mt-2"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDelete(null); setDeleteConfirmation(""); }}>Cancel</Button>
            {!showDelete?.has_history && (
              <Button variant="destructive" onClick={handleDeletePermanent} disabled={deleteConfirmation.toLowerCase() !== showDelete?.name?.toLowerCase()}>
                Delete Permanently
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Report Dialog */}
      <Dialog open={showDuplicateReport} onOpenChange={setShowDuplicateReport}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Committee Duplicate Report</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">Case-insensitive check for duplicate or similar committee names. CPSI will decide on subsequent action.</p>
            {duplicateReport.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No duplicate committees found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Committee ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Active Members</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Assignments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicateReport.map((c) => (
                    <TableRow key={c.committee_id}>
                      <TableCell className="font-mono text-xs">{c.committee_id}</TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell><Badge variant="outline">{c.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                      <TableCell>{c.active_member_count}</TableCell>
                      <TableCell className="text-sm">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{c.assignment_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateReport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Messaging Logs Management (renamed from Email Logs)
const MessagingLogsManagement = () => {
  const [activeTab, setActiveTab] = useState("email");
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Messaging Logs</h1>
        <p className="text-slate-600">View email delivery status and messaging history</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="whatsapp" disabled>
            <Phone className="w-4 h-4 mr-2" />
            WhatsApp
            <Badge variant="outline" className="ml-2 text-xs">Integration pending</Badge>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="email">
          <EmailLogsContent />
        </TabsContent>
        <TabsContent value="whatsapp">
          <Card>
            <CardContent className="py-12 text-center">
              <Phone className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">WhatsApp integration pending</p>
              <p className="text-sm text-slate-400 mt-1">This feature will be available in a future release</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Email Logs Content (extracted from original EmailLogsManagement)
const EmailLogsContent = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchEmails();
  }, [page]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/emails?page=${page}&page_size=20`, { withCredentials: true });
      setEmails(response.data.emails || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch email logs");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      sent: { label: "Sent", className: "bg-teal-100 text-teal-700" },
      failed: { label: "Failed", className: "bg-red-100 text-red-700" },
      test_mode: { label: "Test Mode", className: "bg-amber-100 text-amber-700" }
    };
    const c = config[status] || { label: status, className: "bg-slate-100" };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        {loading ? (
          <p className="text-center py-8 text-slate-500">Loading...</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Sent At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map((email, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{getStatusBadge(email.status)}</TableCell>
                    <TableCell>{email.to_email || email.recipient || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{email.subject}</TableCell>
                    <TableCell className="text-sm">
                      {email.sent_at ? new Date(email.sent_at).toLocaleString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-slate-500">Total: {total} emails</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={emails.length < 20} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// File Access Audit Logs Component (Phase 1)
const FileAccessAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    user_role: "",
    action: "",
    outcome: "",
    date_from: "",
    date_to: "",
    application_id: "",
    user_id: ""
  });

  useEffect(() => {
    fetchLogs();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API}/admin/file-access-logs?${params}`, {
        withCredentials: true
      });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API}/admin/file-access-logs/export?${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `file_access_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Audit logs exported successfully");
    } catch (error) {
      console.error("Error exporting logs:", error);
      toast.error("Failed to export audit logs");
    } finally {
      setExporting(false);
    }
  };

  const getOutcomeBadge = (outcome) => {
    if (outcome === "allowed") {
      return <Badge className="bg-green-100 text-green-700">Allowed</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700">Denied</Badge>;
  };

  const getActionBadge = (action) => {
    const colors = {
      view: "bg-blue-100 text-blue-700",
      download: "bg-purple-100 text-purple-700",
      denied_access: "bg-red-100 text-red-700"
    };
    return <Badge className={colors[action] || "bg-slate-100 text-slate-700"}>{action}</Badge>;
  };

  const clearFilters = () => {
    setFilters({
      user_role: "",
      action: "",
      outcome: "",
      date_from: "",
      date_to: "",
      application_id: "",
      user_id: ""
    });
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="file-access-audit">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading font-bold text-2xl text-slate-900">File Access Audit Logs</h2>
          <p className="text-slate-500">Track document view and download activity</p>
        </div>
        <Button onClick={handleExport} disabled={exporting} data-testid="export-audit-logs">
          <Download className="w-4 h-4 mr-2" />
          {exporting ? "Exporting..." : "Export CSV"}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Select value={filters.user_role} onValueChange={(v) => setFilters(f => ({ ...f, user_role: v }))}>
              <SelectTrigger data-testid="filter-role">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="committee">Committee</SelectItem>
                <SelectItem value="member">Member</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.action} onValueChange={(v) => setFilters(f => ({ ...f, action: v }))}>
              <SelectTrigger data-testid="filter-action">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="download">Download</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.outcome} onValueChange={(v) => setFilters(f => ({ ...f, outcome: v }))}>
              <SelectTrigger data-testid="filter-outcome">
                <SelectValue placeholder="Outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outcomes</SelectItem>
                <SelectItem value="allowed">Allowed</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="From Date"
              value={filters.date_from}
              onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
              data-testid="filter-date-from"
            />

            <Input
              type="date"
              placeholder="To Date"
              value={filters.date_to}
              onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
              data-testid="filter-date-to"
            />

            <Input
              placeholder="Application ID"
              value={filters.application_id}
              onChange={(e) => setFilters(f => ({ ...f, application_id: e.target.value }))}
              data-testid="filter-app-id"
            />

            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No audit logs found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, idx) => (
                  <TableRow key={log.log_id || idx} data-testid={`audit-row-${idx}`}>
                    <TableCell className="text-sm whitespace-nowrap">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{log.actor_name}</p>
                        <p className="text-slate-500 text-xs">{log.actor_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{log.actor_role}</TableCell>
                    <TableCell>
                      <div className="text-sm max-w-[150px] truncate" title={log.document_name}>
                        {log.document_name}
                      </div>
                      <p className="text-xs text-slate-500">{log.document_type}</p>
                    </TableCell>
                    <TableCell>{getActionBadge(log.action)}</TableCell>
                    <TableCell>{getOutcomeBadge(log.outcome)}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[100px] truncate" title={log.access_reason}>
                      {log.access_reason?.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{log.ip_address}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-slate-500">
          Total: {total} logs | Page {page} of {Math.ceil(total / 50) || 1}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={logs.length < 50}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

// Application Details Component
const ApplicationDetails = () => {
  const { id } = require('react-router-dom').useParams();
  const navigate = require('react-router-dom').useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications/${id}/details`, { withCredentials: true });
      setDetails(response.data);
    } catch (error) {
      toast.error("Failed to load application details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="text-center py-8">Loading...</p>;
  if (!details) return <p className="text-center py-8 text-red-500">Application not found</p>;

  const { application, user, documents, corrections, timeline, payment, certificate } = details;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Application Details</h1>
          <p className="text-slate-600">ID: {id}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/applications')}>
          Back to Applications
        </Button>
      </div>

      {/* Applicant Info */}
      <Card>
        <CardHeader><CardTitle>Applicant Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label className="text-slate-500">Name</Label><p className="font-medium">{user?.name || '-'}</p></div>
            <div><Label className="text-slate-500">Email</Label><p>{user?.email || '-'}</p></div>
            <div><Label className="text-slate-500">Phone</Label><p>{user?.phone || '-'}</p></div>
            <div><Label className="text-slate-500">Status</Label><Badge>{application?.status}</Badge></div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Details */}
      {application?.personal_details && (
        <Card>
          <CardHeader><CardTitle>Personal Details</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {Object.entries(application.personal_details).map(([key, value]) => (
                <div key={key}>
                  <Label className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</Label>
                  <p>{String(value) || '-'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents */}
      <Card>
        <CardHeader><CardTitle>Uploaded Documents ({documents?.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {documents?.length === 0 ? (
            <p className="text-slate-500">No documents uploaded</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {documents?.map((doc) => (
                <div key={doc.file_id} className="border rounded-lg p-3">
                  <p className="font-medium text-sm capitalize">{doc.type?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-slate-500">{doc.filename}</p>
                  <div className="flex gap-2 mt-1">
                    <a
                      href={`${API}/secure/file/${doc.file_id}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-blue-700 hover:underline"
                      data-testid={`view-doc-${doc.file_id}`}
                    >
                      <Eye className="w-3 h-3 mr-1" /> View
                    </a>
                    <a
                      href={`${API}/secure/file/${doc.file_id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-teal-700 hover:underline"
                      data-testid={`download-doc-${doc.file_id}`}
                    >
                      <Download className="w-3 h-3 mr-1" /> Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Correction History */}
      {corrections?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Correction Requests</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {corrections.map((c, idx) => (
                <div key={idx} className="border-l-2 border-amber-400 pl-4 py-2">
                  <p className="text-sm">{c.correction_comments}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {c.created_at ? new Date(c.created_at).toLocaleString() : ''} - Status: {c.status}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Info */}
      {payment && (
        <Card>
          <CardHeader><CardTitle>Payment Information</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label className="text-slate-500">Amount</Label><p>₹{payment.amount}</p></div>
              <div><Label className="text-slate-500">Status</Label><Badge>{payment.status}</Badge></div>
              <div><Label className="text-slate-500">Order ID</Label><p className="text-sm font-mono">{payment.razorpay_order_id}</p></div>
              <div><Label className="text-slate-500">Payment ID</Label><p className="text-sm font-mono">{payment.razorpay_payment_id || '-'}</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certificate */}
      {certificate && (
        <Card>
          <CardHeader><CardTitle>Certificate</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm">Certificate generated: {certificate.filename}</p>
            <Button variant="outline" size="sm" className="mt-2">
              <Download className="w-4 h-4 mr-2" /> Download Certificate
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {timeline?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Activity Timeline</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {timeline.slice(0, 10).map((log, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-slate-300 rounded-full" />
                  <span className="font-medium capitalize">{log.action?.replace(/_/g, ' ')}</span>
                  <span className="text-slate-500">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== PHASE 2: GENERIC CERTIFICATES UI ==============
const GenericCertificatesManagement = () => {
  const [certificates, setCertificates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [previewCert, setPreviewCert] = useState(null);
  const [sendDialog, setSendDialog] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [formData, setFormData] = useState({
    recipient_name: "",
    certificate_title: "",
    purpose: "",
    issue_date: new Date().toISOString().split('T')[0],
    certificate_reference: "",
    template_id: "",
    signers: [{ name: "", designation: "" }]
  });

  useEffect(() => {
    fetchCertificates();
    fetchTemplates();
  }, []);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/certificates/generic`, { withCredentials: true });
      setCertificates(response.data.certificates || []);
    } catch (error) {
      toast.error("Failed to fetch certificates");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/certificate-templates`, { withCredentials: true });
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const activeTemplates = templates.filter(t => t.is_active);

  const handleCreate = async () => {
    if (!formData.recipient_name || !formData.certificate_title || !formData.template_id) {
      toast.error("Recipient name, title, and template are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/certificates/generic`, formData, { withCredentials: true });
      toast.success("Generic certificate created");
      setShowCreate(false);
      setFormData({ recipient_name: "", certificate_title: "", purpose: "", issue_date: new Date().toISOString().split('T')[0], certificate_reference: "", template_id: "", signers: [{ name: "", designation: "" }] });
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Creation failed");
    }
  };

  const handleApprove = async (certId) => {
    try {
      await axios.post(`${API}/admin/certificates/generic/${certId}/approve`, {}, { withCredentials: true });
      toast.success("Certificate approved");
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Approval failed");
    }
  };

  const handleSend = async () => {
    if (!sendEmail) {
      toast.error("Email is required");
      return;
    }
    try {
      await axios.post(`${API}/admin/certificates/generic/${sendDialog.certificate_id}/send?recipient_email=${encodeURIComponent(sendEmail)}`, {}, { withCredentials: true });
      toast.success("Certificate sent");
      setSendDialog(null);
      setSendEmail("");
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Send failed");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      approved: "bg-teal-100 text-teal-700",
      sent: "bg-blue-100 text-blue-700"
    };
    return <Badge className={styles[status] || "bg-gray-100"}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="generic-certificates-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Generic Certificates</h1>
          <p className="text-slate-600">Create certificates not tied to membership applications</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchCertificates}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="create-generic-cert-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Certificate
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading...</p>
          ) : certificates.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No generic certificates created yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certificates.map((cert) => (
                  <TableRow key={cert.certificate_id}>
                    <TableCell className="font-medium">{cert.recipient_name}</TableCell>
                    <TableCell>{cert.certificate_title}</TableCell>
                    <TableCell className="font-mono text-sm">{cert.certificate_reference}</TableCell>
                    <TableCell>{cert.issue_date}</TableCell>
                    <TableCell>{getStatusBadge(cert.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => window.open(`${API}/admin/certificates/generic/${cert.certificate_id}/preview`, '_blank')} title="Preview">
                          <Eye className="w-4 h-4 text-slate-600" />
                        </Button>
                        {cert.status === "draft" && (
                          <Button variant="ghost" size="sm" onClick={() => handleApprove(cert.certificate_id)} title="Approve">
                            <CheckCircle className="w-4 h-4 text-teal-600" />
                          </Button>
                        )}
                        {cert.status === "approved" && (
                          <Button variant="ghost" size="sm" onClick={() => setSendDialog(cert)} title="Send">
                            <Send className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Certificate Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Generic Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Recipient Name *</Label>
              <Input value={formData.recipient_name} onChange={(e) => setFormData({...formData, recipient_name: e.target.value})} placeholder="Enter recipient name" data-testid="generic-cert-recipient" />
            </div>
            <div className="space-y-2">
              <Label>Certificate Title *</Label>
              <Input value={formData.certificate_title} onChange={(e) => setFormData({...formData, certificate_title: e.target.value})} placeholder="e.g., Certificate of Appreciation" data-testid="generic-cert-title" />
            </div>
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Textarea value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} placeholder="Optional purpose description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Date *</Label>
                <Input type="date" value={formData.issue_date} onChange={(e) => setFormData({...formData, issue_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Reference (Optional)</Label>
                <Input value={formData.certificate_reference} onChange={(e) => setFormData({...formData, certificate_reference: e.target.value})} placeholder="Auto-generated if empty" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Template *</Label>
              <Select value={formData.template_id} onValueChange={(v) => setFormData({...formData, template_id: v})}>
                <SelectTrigger data-testid="generic-cert-template-select">
                  <SelectValue placeholder="Select active template" />
                </SelectTrigger>
                <SelectContent>
                  {activeTemplates.length === 0 ? (
                    <SelectItem value="none" disabled>No active templates</SelectItem>
                  ) : (
                    activeTemplates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>{t.name} (Active)</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Only active templates can be used for new certificates</p>
            </div>
            <div className="space-y-2">
              <Label>Signer Details</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input value={formData.signers[0]?.name || ""} onChange={(e) => setFormData({...formData, signers: [{...formData.signers[0], name: e.target.value}]})} placeholder="Signer name" />
                <Input value={formData.signers[0]?.designation || ""} onChange={(e) => setFormData({...formData, signers: [{...formData.signers[0], designation: e.target.value}]})} placeholder="Designation" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="submit-generic-cert">Create Certificate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Certificate Dialog */}
      <Dialog open={!!sendDialog} onOpenChange={() => { setSendDialog(null); setSendEmail(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-slate-600">Send <strong>{sendDialog?.certificate_title}</strong> to:</p>
            <div className="space-y-2">
              <Label>Recipient Email *</Label>
              <Input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="Enter email address" data-testid="send-cert-email" />
            </div>
            <p className="text-sm text-amber-600">Only send to CPSI test accounts during testing.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(null)}>Cancel</Button>
            <Button onClick={handleSend} data-testid="confirm-send-cert">Send Certificate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== PHASE 2: REFUND CONTROLS UI ==============
const RefundControlsManagement = () => {
  const [refunds, setRefunds] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [approveDialog, setApproveDialog] = useState(null);
  const [approvalComments, setApprovalComments] = useState("");
  const [requestData, setRequestData] = useState({ payment_id: "", amount: "", reason: "" });

  useEffect(() => {
    fetchRefunds();
    fetchPayments();
  }, []);

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/refunds`, { withCredentials: true });
      setRefunds(response.data.refunds || []);
    } catch (error) {
      toast.error("Failed to fetch refunds");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments/refund-eligible`, { withCredentials: true });
      setPayments(response.data.payments || []);
    } catch (error) {
      // Fallback to old endpoint if new one doesn't exist
      try {
        const fallbackResponse = await axios.get(`${API}/admin/payments?status=completed&page_size=100`, { withCredentials: true });
        setPayments(fallbackResponse.data.payments || []);
      } catch (e) {
        console.error("Failed to fetch payments");
      }
    }
  };

  const handleRequestRefund = async () => {
    if (!requestData.payment_id || !requestData.amount || !requestData.reason) {
      toast.error("All fields are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/refunds/request`, {
        payment_id: requestData.payment_id,
        amount: parseInt(requestData.amount) * 100,
        reason: requestData.reason
      }, { withCredentials: true });
      toast.success("Refund request created");
      setShowRequest(false);
      setRequestData({ payment_id: "", amount: "", reason: "" });
      fetchRefunds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Request failed");
    }
  };

  const handleApproval = async (action) => {
    try {
      await axios.post(`${API}/admin/refunds/${approveDialog.refund_id}/approve`, {
        refund_request_id: approveDialog.refund_id,
        action: action,
        comments: approvalComments
      }, { withCredentials: true });
      toast.success(`Refund ${action}d`);
      setApproveDialog(null);
      setApprovalComments("");
      fetchRefunds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Approval failed");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_approval: "bg-amber-100 text-amber-700",
      approved: "bg-teal-100 text-teal-700",
      pending_provider_confirmation: "bg-blue-100 text-blue-700",
      processed: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
      provider_error: "bg-red-100 text-red-700"
    };
    return <Badge className={styles[status] || "bg-gray-100"}>{status?.replace(/_/g, ' ')}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="refund-controls-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Refund Controls</h1>
          <p className="text-slate-600">Manage refund requests with maker-checker approval for high values</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRefunds}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowRequest(true)} data-testid="request-refund-btn">
            <Plus className="w-4 h-4 mr-2" /> Request Refund
          </Button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Maker-Checker:</strong> Refunds above ₹5,000 require approval from a different admin. Requester cannot approve their own request.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading...</p>
          ) : refunds.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No refund requests</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Refund ID</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Payment ID</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {refunds.map((refund) => (
                  <TableRow key={refund.refund_id}>
                    <TableCell className="font-mono text-sm">{refund.refund_id}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{refund.member_name}</p>
                        <p className="text-slate-500">{refund.member_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{refund.payment_id}</TableCell>
                    <TableCell>₹{(refund.amount / 100).toFixed(2)}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={refund.reason}>{refund.reason}</TableCell>
                    <TableCell>{getStatusBadge(refund.status)}</TableCell>
                    <TableCell className="text-right">
                      {refund.status === "pending_approval" && (
                        <Button variant="ghost" size="sm" onClick={() => setApproveDialog(refund)} title="Review" data-testid={`review-refund-${refund.refund_id}`}>
                          <Eye className="w-4 h-4 text-slate-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Request Refund Dialog */}
      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Payment *</Label>
              {payments.length === 0 ? (
                <div className="bg-slate-50 border rounded-lg p-4 text-center">
                  <p className="text-slate-600 text-sm">No completed verified payments are currently eligible for refund.</p>
                  <p className="text-xs text-slate-500 mt-1">Eligible payments must be completed, verified, and not already refunded or have an active refund request.</p>
                </div>
              ) : (
                <Select value={requestData.payment_id} onValueChange={(v) => {
                  const selectedPayment = payments.find(p => p.payment_id === v);
                  setRequestData({...requestData, payment_id: v, amount: selectedPayment ? String(selectedPayment.amount / 100) : ""});
                }}>
                  <SelectTrigger data-testid="refund-payment-select">
                    <SelectValue placeholder="Select completed payment" />
                  </SelectTrigger>
                  <SelectContent>
                    {payments.map(p => (
                      <SelectItem key={p.payment_id} value={p.payment_id}>
                        <span className="flex flex-col">
                          <span>{p.payment_id} - ₹{(p.amount / 100).toFixed(2)}</span>
                          <span className="text-xs text-slate-500">{p.user_name || p.user_id} | {p.type} | {p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Refund Amount (₹) *</Label>
              <Input type="number" value={requestData.amount} onChange={(e) => setRequestData({...requestData, amount: e.target.value})} placeholder="Enter amount in INR" data-testid="refund-amount" />
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea value={requestData.reason} onChange={(e) => setRequestData({...requestData, reason: e.target.value})} placeholder="Enter refund reason" data-testid="refund-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequest(false)}>Cancel</Button>
            <Button onClick={handleRequestRefund} disabled={payments.length === 0} data-testid="submit-refund-request">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={!!approveDialog} onOpenChange={() => { setApproveDialog(null); setApprovalComments(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Refund Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
              <p><strong>Refund ID:</strong> {approveDialog?.refund_id}</p>
              <p><strong>Amount:</strong> ₹{approveDialog ? (approveDialog.amount / 100).toFixed(2) : 0}</p>
              <p><strong>Reason:</strong> {approveDialog?.reason}</p>
              <p><strong>Requested by:</strong> {approveDialog?.requested_by}</p>
            </div>
            <div className="space-y-2">
              <Label>Comments (optional)</Label>
              <Textarea value={approvalComments} onChange={(e) => setApprovalComments(e.target.value)} placeholder="Add approval/rejection comments" />
            </div>
            <p className="text-xs text-amber-600">Note: You cannot approve your own refund request.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleApproval("reject")} data-testid="reject-refund">Reject</Button>
            <Button onClick={() => handleApproval("approve")} data-testid="approve-refund">Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== PHASE 2: RENEWAL MANAGEMENT UI ==============
const RenewalManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [termFilter, setTermFilter] = useState("");
  const [extendDialog, setExtendDialog] = useState(null);
  const [ledgerDialog, setLedgerDialog] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [extensionData, setExtensionData] = useState({ action: "extend", months: 12, is_lifetime: false, reason: "" });
  const [renewalPlans, setRenewalPlans] = useState([]);

  useEffect(() => {
    fetchRenewals();
    fetchPlans();
  }, [statusFilter, termFilter]);

  const fetchRenewals = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/renewals?page_size=100`;
      if (statusFilter) url += `&status_filter=${statusFilter}`;
      if (termFilter) url += `&term_filter=${termFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error("Failed to fetch renewals");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/admin/renewal-plans`, { withCredentials: true });
      setRenewalPlans(response.data.plans || []);
    } catch (error) {
      console.error("Failed to fetch plans");
    }
  };

  const fetchLedger = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/renewal-ledger/${userId}`, { withCredentials: true });
      setLedgerData(response.data);
      setLedgerDialog(userId);
    } catch (error) {
      toast.error("Failed to fetch ledger");
    }
  };

  const handleExtend = async () => {
    if (!extensionData.reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      await axios.post(`${API}/admin/renewals/manual-extend`, {
        user_id: extendDialog.user_id,
        ...extensionData
      }, { withCredentials: true });
      toast.success("Membership extended");
      setExtendDialog(null);
      setExtensionData({ action: "extend", months: 12, is_lifetime: false, reason: "" });
      fetchRenewals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Extension failed");
    }
  };

  const handleExport = async () => {
    try {
      let url = `${API}/admin/renewals/export`;
      const params = [];
      if (statusFilter) params.push(`status_filter=${statusFilter}`);
      if (termFilter) params.push(`term_filter=${termFilter}`);
      if (params.length) url += `?${params.join('&')}`;
      
      window.open(url, '_blank');
      toast.success("Export initiated");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="renewal-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Renewal Management</h1>
          <p className="text-slate-600">Manage membership validity and renewals</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-renewals">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" onClick={fetchRenewals}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="renewal-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Members</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="due_30">Due in 30 days</SelectItem>
            <SelectItem value="due_60">Due in 60 days</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
          </SelectContent>
        </Select>
        <Select value={termFilter || "all"} onValueChange={(v) => setTermFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="renewal-term-filter">
            <SelectValue placeholder="Filter by term" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Terms</SelectItem>
            <SelectItem value="annual">Annual</SelectItem>
            <SelectItem value="multi_year">Multi-Year</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading...</p>
          ) : members.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No members found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Membership Type</TableHead>
                  <TableHead>Term</TableHead>
                  <TableHead>Validity / Renewal Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.user_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{member.user_name}</p>
                        <p className="text-sm text-slate-500">{member.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{member.membership_type}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{member.membership_term || 'annual'}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className={member.validity_display?.includes('Expired') ? 'text-red-600' : member.validity_display?.includes('Pending') ? 'text-amber-600' : 'text-slate-700'}>
                        {member.validity_display}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={member.membership_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100'}>{member.membership_status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => fetchLedger(member.user_id)} title="View Ledger" data-testid={`view-ledger-${member.user_id}`}>
                          <History className="w-4 h-4 text-slate-600" />
                        </Button>
                        {member.membership_term !== 'lifetime' && (
                          <Button variant="ghost" size="sm" onClick={() => setExtendDialog(member)} title="Extend" data-testid={`extend-member-${member.user_id}`}>
                            <RefreshCw className="w-4 h-4 text-teal-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Manual Extension Dialog */}
      <Dialog open={!!extendDialog} onOpenChange={() => { setExtendDialog(null); setExtensionData({ action: "extend", months: 12, is_lifetime: false, reason: "" }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manual Membership Extension</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p><strong>{extendDialog?.user_name}</strong></p>
              <p className="text-slate-600">Current validity: {extendDialog?.validity_display}</p>
            </div>
            <div className="space-y-2">
              <Label>Action Type *</Label>
              <Select value={extensionData.action} onValueChange={(v) => setExtensionData({...extensionData, action: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="extend">Extension</SelectItem>
                  <SelectItem value="waiver">Waiver</SelectItem>
                  <SelectItem value="correction">Correction</SelectItem>
                  <SelectItem value="override">Override</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={extensionData.is_lifetime} onCheckedChange={(v) => setExtensionData({...extensionData, is_lifetime: v, months: v ? 0 : 12})} data-testid="lifetime-toggle" />
              <Label>Lifetime Membership</Label>
            </div>
            {!extensionData.is_lifetime && (
              <div className="space-y-2">
                <Label>Extension Period (months) *</Label>
                <Select value={String(extensionData.months)} onValueChange={(v) => setExtensionData({...extensionData, months: parseInt(v)})}>
                  <SelectTrigger data-testid="extension-months">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">12 months (1 year)</SelectItem>
                    <SelectItem value="24">24 months (2 years)</SelectItem>
                    <SelectItem value="36">36 months (3 years)</SelectItem>
                    <SelectItem value="6">6 months (custom)</SelectItem>
                    <SelectItem value="18">18 months (custom)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea value={extensionData.reason} onChange={(e) => setExtensionData({...extensionData, reason: e.target.value})} placeholder="Enter reason for this action (required for audit)" data-testid="extension-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog(null)}>Cancel</Button>
            <Button onClick={handleExtend} data-testid="confirm-extend">Apply Extension</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ledger Dialog */}
      <Dialog open={!!ledgerDialog} onOpenChange={() => { setLedgerDialog(null); setLedgerData(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Renewal Ledger</DialogTitle>
          </DialogHeader>
          {ledgerData && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><strong>{ledgerData.member.name}</strong> ({ledgerData.member.email})</p>
                <p className="text-slate-600">Current validity: {ledgerData.member.current_valid_until ? new Date(ledgerData.member.current_valid_until).toLocaleDateString() : ledgerData.member.membership_term === 'lifetime' ? 'Life-Time' : 'Not set'}</p>
              </div>
              {ledgerData.ledger_entries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Old Validity</TableHead>
                      <TableHead>New Validity</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerData.ledger_entries.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">{new Date(entry.created_at).toLocaleString()}</TableCell>
                        <TableCell><Badge variant="outline">{entry.entry_type}</Badge></TableCell>
                        <TableCell className="text-sm">{entry.old_valid_until ? new Date(entry.old_valid_until).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="text-sm">{entry.new_valid_until === 'lifetime' ? 'Life-Time' : entry.new_valid_until ? new Date(entry.new_valid_until).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{entry.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center py-4 text-slate-500">No ledger entries</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== CERTIFICATES HUB ==============
const CertificatesHub = () => {
  return (
    <div className="space-y-6" data-testid="certificates-hub">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Certificates</h1>
        <p className="text-slate-600">Manage certificate templates and member certificates</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/certificates/templates">
          <Card className="hover:border-purple-200 hover:shadow-card-hover transition-all cursor-pointer border-purple-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-600" />
                Certificate Templates
              </CardTitle>
              <CardDescription>
                Upload blank certificate backgrounds, configure field positions and signers, activate templates.
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/admin/certificates/member">
          <Card className="hover:border-teal-200 hover:shadow-card-hover transition-all cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-teal-600" />
                Member Certificates
              </CardTitle>
              <CardDescription>
                Review, approve, and send membership certificates to paid members.
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/admin/certificates/generic">
          <Card className="hover:border-blue-200 hover:shadow-card-hover transition-all cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-blue-600" />
                Generic Certificates
              </CardTitle>
              <CardDescription>
                Standalone certificates not linked to membership (appreciation, participation, etc).
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
      </div>
    </div>
  );
};

// ============== SETTINGS HUB ==============
const SettingsHub = () => {
  return (
    <div className="space-y-6" data-testid="settings-hub">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Settings</h1>
        <p className="text-slate-600">System configuration and maintenance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/settings/integrations">
          <Card className="hover:border-teal-200 hover:shadow-card-hover transition-all cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Integrations
              </CardTitle>
              <CardDescription>
                Payment gateway, email service, and API configurations.
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/admin/settings/zoho">
          <Card className="hover:border-teal-200 hover:shadow-card-hover transition-all cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Zoho One Integrations
              </CardTitle>
              <CardDescription>
                Planned integrations roadmap. All integrations are currently Planned / Not Connected.
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/admin/settings/data-maintenance">
          <Card className="hover:border-amber-200 hover:shadow-card-hover transition-all cursor-pointer border-amber-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Data Maintenance
              </CardTitle>
              <CardDescription>
                Quarantined profiles - legacy records without matching login accounts. View-only unless CPSI approves action.
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
      </div>
    </div>
  );
};

// ============== ORPHAN PROFILE MANAGEMENT (Admin Only) ==============
const OrphanProfileManagement = () => {
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionType, setActionType] = useState("quarantine");
  const [actionReason, setActionReason] = useState("");
  const [linkUserId, setLinkUserId] = useState("");

  useEffect(() => {
    fetchOrphans();
  }, []);

  const fetchOrphans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orphan-profiles`, { withCredentials: true });
      setOrphans(response.data.orphan_profiles || []);
    } catch (error) {
      toast.error("Failed to fetch orphan profiles");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!actionReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    if (actionType === "link_user" && !linkUserId.trim()) {
      toast.error("Target user ID is required for linking");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/orphan-profiles/${actionDialog.user_id}/action`, {
        action: actionType,
        reason: actionReason,
        link_to_user_id: actionType === "link_user" ? linkUserId : null
      }, { withCredentials: true });
      toast.success(`Profile ${actionType === 'quarantine' ? 'quarantined' : actionType === 'link_user' ? 'linked' : 'retained'}`);
      setActionDialog(null);
      setActionReason("");
      setLinkUserId("");
      fetchOrphans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="orphan-profile-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Data Maintenance — Quarantined Profiles</h1>
          <p className="text-slate-600">Legacy profile records without a matching login account</p>
        </div>
        <Button variant="outline" onClick={fetchOrphans}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900">What are Quarantined Profiles?</h3>
              <p className="text-sm text-amber-800 mt-1">
                These are legacy profile records without a matching login account. They are quarantined, 
                <strong> cannot sign in</strong>, are <strong>not public</strong>, and are <strong>excluded</strong> from 
                directory, renewals, payments, and normal exports. No action will be taken without separate CPSI approval.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading...</p>
          ) : orphans.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No orphan profiles found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User ID</TableHead>
                  <TableHead>Membership #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Associated Data</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphans.map((orphan) => (
                  <TableRow key={orphan.user_id} className={orphan.is_quarantined ? "bg-amber-50" : ""}>
                    <TableCell className="font-mono text-sm">{orphan.user_id}</TableCell>
                    <TableCell>{orphan.membership_number || '-'}</TableCell>
                    <TableCell>{orphan.membership_type}</TableCell>
                    <TableCell>
                      {orphan.is_quarantined ? (
                        <Badge className="bg-amber-100 text-amber-700">Quarantined</Badge>
                      ) : (
                        <Badge variant="outline">{orphan.membership_status || 'none'}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      Payments: {orphan.associated_payments}, Certs: {orphan.associated_certificates}, Ledger: {orphan.renewal_ledger_entries}
                    </TableCell>
                    <TableCell className="text-right">
                      {!orphan.is_quarantined && (
                        <Button variant="ghost" size="sm" onClick={() => setActionDialog(orphan)} title="Take Action" data-testid={`orphan-action-${orphan.user_id}`}>
                          <Settings className="w-4 h-4 text-slate-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => { setActionDialog(null); setActionReason(""); setLinkUserId(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Orphan Profile Action</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p><strong>User ID:</strong> {actionDialog?.user_id}</p>
              <p><strong>Membership #:</strong> {actionDialog?.membership_number || 'None'}</p>
              <p><strong>Application ID:</strong> {actionDialog?.application_id || 'None'}</p>
            </div>
            <div className="space-y-2">
              <Label>Action *</Label>
              <Select value={actionType} onValueChange={setActionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarantine">Quarantine (exclude from all lists)</SelectItem>
                  <SelectItem value="link_user">Link to Existing User</SelectItem>
                  <SelectItem value="retain">Retain with Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {actionType === "link_user" && (
              <div className="space-y-2">
                <Label>Link to User ID *</Label>
                <Input value={linkUserId} onChange={(e) => setLinkUserId(e.target.value)} placeholder="Enter existing user_id" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea value={actionReason} onChange={(e) => setActionReason(e.target.value)} placeholder="Enter reason for this action (required for audit)" />
            </div>
            <p className="text-xs text-slate-500">
              This action will be logged. Records are preserved for audit.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={handleAction}>Apply Action</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== PHASE 3: ZOHO ONE INTEGRATIONS UI (STATIC) ==============
const ZohoIntegrationsUI = () => {
  const zohoServices = [
    { name: "Zoho CRM", description: "Customer relationship management for member tracking", icon: Users },
    { name: "Zoho Campaigns", description: "Email marketing and member communication", icon: Mail },
    { name: "Zoho Backstage", description: "Event management for CPSI conferences", icon: Building2 },
    { name: "Zoho Webinar / Zoho Meeting", description: "Virtual events and webinars", icon: Globe },
    { name: "Zoho Learn", description: "Learning management for member education", icon: Award },
    { name: "Zoho Payments", description: "Payment processing integration", icon: CreditCard },
    { name: "Zoho Flow", description: "Workflow automation between services", icon: RefreshCw }
  ];

  return (
    <div className="space-y-6" data-testid="zoho-integrations-ui">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Zoho One Integrations</h1>
        <p className="text-slate-600">Planned integrations roadmap – UI preview only</p>
      </div>

      {/* Architecture Note */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-900 mb-2">Architecture Note</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc ml-4">
            <li>CPSI portal remains the <strong>source of truth</strong> for all member data</li>
            <li>Future integrations will use stable <strong>CPSI Member ID</strong> as the primary identifier</li>
            <li>Members will <strong>NOT</strong> receive Zoho One administrative access</li>
            <li>Any future data sharing with Zoho services will be <strong>limited and separately approved</strong></li>
          </ul>
        </CardContent>
      </Card>

      {/* Service Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {zohoServices.map((service, index) => (
          <Card key={index} className="border border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <service.icon className="w-6 h-6 text-slate-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{service.name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{service.description}</p>
                  <Badge className="mt-3 bg-slate-100 text-slate-600" data-testid={`zoho-status-${index}`}>
                    Planned / Not Connected
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Implementation Note */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900">Implementation Status</h3>
              <p className="text-sm text-amber-800 mt-1">
                This is a <strong>UI preview only</strong>. No Zoho APIs, OAuth, webhooks, or data synchronization have been implemented. 
                No credentials are required or stored. No network calls to Zoho services are made.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const location = useLocation();
  
  const navItems = [
    { path: "/admin", label: "Overview", icon: BarChart3 },
    { path: "/admin/applications", label: "Applications", icon: FileText },
    { path: "/admin/applications-master", label: "Application Master", icon: ClipboardList },
    { path: "/admin/seeded", label: "Seeded Members", icon: Users },
    { path: "/admin/deleted", label: "Deleted Members", icon: UserX },
    { path: "/admin/committees", label: "Committees", icon: Building2 },
    { path: "/admin/payments", label: "Payments", icon: CreditCard },
    { path: "/admin/refunds", label: "Refund Controls", icon: RotateCcw },
    { path: "/admin/renewals", label: "Renewal Management", icon: RefreshCw },
    { path: "/admin/messaging", label: "Messaging Logs", icon: Mail },
    { path: "/admin/audit-logs", label: "File Audit Logs", icon: Shield },
    { path: "/admin/directory", label: "Directory", icon: FileSpreadsheet },
    { path: "/admin/certificates", label: "Certificates", icon: Award },
    { path: "/admin/specialities", label: "Specialities", icon: Globe },
    { path: "/admin/settings", label: "Settings", icon: Settings },
  ];

  const isActive = (path) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 min-h-screen sticky top-0">
          <div className="p-6">
            <h2 className="font-heading font-bold text-lg text-slate-900">Admin Panel</h2>
            <p className="text-sm text-slate-500">CPSI Secretariat</p>
          </div>
          <nav className="px-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  isActive(item.path)
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-hidden p-8">
          <Routes>
            <Route path="/" element={<AdminOverview />} />
            <Route path="/applications" element={<ApplicationsManagement />} />
            <Route path="/applications-master" element={<ApplicationMasterView />} />
            <Route path="/applications/:id" element={<ApplicationDetails />} />
            <Route path="/seeded" element={<SeededMembersManagement />} />
            <Route path="/deleted" element={<DeletedMembersManagement />} />
            <Route path="/committees" element={<CommitteesManagement />} />
            <Route path="/payments" element={<PaymentsManagement />} />
            <Route path="/refunds" element={<RefundControlsManagement />} />
            <Route path="/renewals" element={<RenewalManagement />} />
            <Route path="/messaging" element={<MessagingLogsManagement />} />
            <Route path="/audit-logs" element={<FileAccessAuditLogs />} />
            <Route path="/directory" element={<DirectoryManagement />} />
            <Route path="/certificates" element={<CertificatesHub />} />
            <Route path="/certificates/member" element={<CertificatesReview />} />
            <Route path="/certificates/generic" element={<GenericCertificatesManagement />} />
            <Route path="/certificates/templates" element={<CertificateManagement />} />
            <Route path="/specialities" element={<SpecialitiesManagement />} />
            <Route path="/settings" element={<SettingsHub />} />
            <Route path="/settings/integrations" element={<IntegrationSettings />} />
            <Route path="/settings/zoho" element={<ZohoIntegrationsUI />} />
            <Route path="/settings/data-maintenance" element={<OrphanProfileManagement />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
